/** Section 3.2 - the adventure kernel's single authoritative entry point.
 *
 * Pipeline, in this order and no other: structural validation, legality, clone
 * the whole state (D1), deterministic resolution consuming RNG from state,
 * mutate the clone, emit events, invariant check in DEV, hash under
 * `opts.verify`, bump `version`, commit.
 *
 * Illegality is returned. A thrown error from anything below this line is a bug
 * in the rules, not a bad command (Section 6).
 */

import type { BattleCommand, ExecuteError, GameCommand, Reason } from "../commands.js";
import { illegal, malformed } from "../commands.js";
import type { GameEvent } from "../events.js";
import type { ContentRegistry, MagicGroundDef } from "../content/registry.js";
import type {
  ArmyId, ArtifactDefId, BuildingDefId, CreatureDefId, HeroDefId, HeroId, ObjectId,
  PlayerId, SkillDefId, SpellDefId, TownId,
} from "../ids.js";
import {
  asBuildingDefId, asCreatureDefId, asHeroDefId, asModifierSrcId, asSkillDefId,
  asSpellDefId, asSubskillDefId,
} from "./ids.js";
import type { RngStreamState } from "../rng/pcg32.js";
import { RNG_STREAMS } from "../rng/streams.js";
import type { StateHash } from "../hash/index.js";
import { hashState } from "../hash/index.js";
import type { Result } from "../result.js";
import { Err, Ok } from "../result.js";
import { BP_ONE, FP_ONE, clampInt } from "../numeric.js";
import type { CreatureModifiers, Modifier, StatKey } from "../modifiers.js";
import { resolveStat } from "../modifiers.js";
import type {
  ArmySlot, ArmyState, GameState, HeroState, InteractionResponse, MapObjectState,
  ResourceKind, ResumeToken, TownState,
} from "../state.js";
import { RESOURCES, TERRAINS } from "../state.js";
import { deepClone } from "../util/clone.js";
import { entityKeys } from "../util/iter.js";
import {
  cellCoords, cellEnterable, indexOf, isWaterCell, occupantAt, stepCost,
} from "../map/create.js";
import { FOG_UNSEEN, visibilityAt } from "../map/fog.js";
import { startBattle, type BattleHeroInput } from "../battle/init.js";
import { wetKindFromTerrain } from "../battle/wet.js";
import { FIELD_OBSTACLE_STREAM, fieldObstacles } from "../battle/field.js";
import { executeBattleCommand, openBattle } from "../battle/execute.js";
import { raisingBarred } from "../battle/spells.js";
import type { Obstacle } from "../battle/state.js";
import {
  BF_HEIGHT, hexIndex, isAlive, SIDE_ATTACKER, SIDE_DEFENDER,
} from "../battle/state.js";
import { activeStack } from "../battle/order.js";
import {
  STAT_DAMAGE_DEALT, STAT_DAMAGE_MELEE, STAT_DAMAGE_RANGED, STAT_DAMAGE_TAKEN,
  STAT_RANGE_PENALTY_CUT,
} from "../battle/damage.js";
import {
  sealedSchoolStat,
  STAT_EXTRA_RETALIATION, STAT_FIRE_SHIELD, STAT_REGEN, STAT_RUNE_CAP,
  STAT_IGNORE_IMMUNITY, STAT_MAGIC_MIRROR, STAT_RUNES, STAT_MAGIC_RESIST, STAT_NO_ESCAPE, STAT_NO_RAISE, STAT_SPELL_LEVEL_CAP, STAT_SPELL_LEVEL_FLOOR,
  spellImmunityStat,
} from "../battle/spells.js";
import type { Ctx } from "./ctx.js";
import {
  addResource, closePending, dropHero, makeCtx, mintObjectId, missingResource,
  patchArmy, patchHero, patchObject, patchPlayer, patchTown, putObject, rollChanceBp,
  tokenPath, tokenStr, dropArmy,
} from "./ctx.js";
import {
  ARMY_SLOTS, CODED_SKILLS, MAX_HEROES_PER_PLAYER, addCreatures, armyEmpty,
  armyGoldCost, dailyMovement, emptyArmySlots, equippedSlots, giveArtifact,
  grantExperience, hasFreeSlotFor,
  armyIsFactionPure, learnSkill, maxMana, moveRulesFor, pickSubskill, primaryFor,
  retuneMana, setSlot, settleAllLevelUps,
  settleLevelUps,
  heroModifiers, learnIllegality, learnSpell, skillLevelOf, statFor,
} from "./hero.js";
import { freeSlotFor, slotAccepts, slotIsFree, withSlots } from "./slots.js";
import {
  creatureBonusesFor, skillModifiersFor, specialtyMachines, spellSpecialtyFp,
} from "./specialty.js";
import { defenceModifiers, kingdomCreatureBonuses } from "./buildings.js";
import {
  assemble, assembleIllegality, disassemble, disassembleIllegality,
} from "./assemble.js";
import {
  DIG_SITE_OBJECT, GRAIL_ARTIFACT, GRAIL_SLOT, carriesGrail, diggableCell,
  grailBearerIn, grailCell,
  spendGrail,
} from "./grail.js";
import { researchCandidates, researchCost, researchIllegality } from "./research.js";
import {
  addBuilding, arriveInTown, availableOf, buildIllegality, builtDefs, demoteCapitol,
  fillGuild, recruitCost, spendAvailable, townUpgradeOffers, upgradableArmies,
} from "./town.js";
import { sendCaravan } from "./caravan.js";
import { upgradeOf } from "./upgrade.js";
import { castAdventureSpell, moveObject,
} from "./spells.js";
import {
  entryIllegality, grantBankReward, payloadOf, portalExits, removeObject,
  resolveTreasureChoice, resumeService, teleportTo, tradingPosts, visitObject,
  payStr, payNum, openGarrison, payFlag,
} from "./objects.js";
import { createHero } from "./create.js";
import { MAGIC_SRC_PREFIX } from "../battle/spells.js";
import { setPayload } from "./payload.js";
import { declareWinner, endTurn, evaluateVictory } from "./turn.js";
import { refreshMemory, revealStep } from "./visibility.js";

// ------------------------------------------------------------------ result

export interface CommandResult<S> {
  readonly state: S;
  readonly events: readonly GameEvent[];
  readonly hash?: StateHash;
}

export interface ExecuteOpts {
  readonly verify?: boolean;
  /** Run the invariant sweep. Defaults on, because it is cheap next to a clone. */
  readonly dev?: boolean;
}

/** A hero costs 2500 gold in the tavern, as in the classic. */
export const HERO_HIRE_COST = 2500;
/** Surrendering buys the army back at its gold price, cut by Diplomacy. */
export const SURRENDER_DIPLOMACY_CUT_BP: readonly number[] = Object.freeze([0, 2000, 4000, 6000]);
/**
 * Marketplace efficiency in basis points by the number of Marketplaces the
 * player owns. The reference states only that rates improve with each
 * Marketplace; these are the values the exchange is modelled on.
 */
export const MARKET_EFFICIENCY_BP: readonly number[] =
  Object.freeze([3000, 4000, 5000, 5500, 6000, 6500, 7000, 7500, 8000, 8500, 9000]);

/**
 * Offense / Archery / Armorer percentages, used only when the content declares
 * no modifier for that skill level. The classic's numbers, kept here so a data gap
 * degrades to the right rule rather than to no rule at all.
 */
const SKILL_PCT_FALLBACK: Readonly<Record<string, readonly number[]>> = Object.freeze({
  offense: [0, 10, 20, 30],
  archery: [0, 10, 25, 50],
  armorer: [0, 5, 10, 15],
});

// --------------------------------------------------------------- structural

function structural(cmd: GameCommand): string[] {
  const issues: string[] = [];
  const id = (v: unknown, name: string): void => {
    if (typeof v !== "string" || v.length === 0) issues.push(`${name} must be a non-empty id`);
  };
  const int = (v: unknown, name: string, lo = 0): void => {
    if (typeof v !== "number" || !Number.isInteger(v) || v < lo) {
      issues.push(`${name} must be an integer >= ${lo}`);
    }
  };
  const slot = (v: unknown, name: string): void => {
    if (typeof v !== "number" || !Number.isInteger(v) || v < 0 || v >= ARMY_SLOTS) {
      issues.push(`${name} must be a slot index 0..${ARMY_SLOTS - 1}`);
    }
  };

  switch (cmd.type) {
    case "END_TURN": id(cmd.player, "player"); break;
    case "MOVE_HERO":
      id(cmd.hero, "hero");
      if (!Array.isArray(cmd.path)) issues.push("path must be an array");
      else for (let i = 0; i < cmd.path.length; i++) int(cmd.path[i], `path[${i}]`);
      break;
    case "HERO_TO_TOWN": id(cmd.hero, "hero"); id(cmd.town, "town"); break;
    case "BUILD": id(cmd.town, "town"); id(cmd.building, "building"); break;
    case "TOGGLE_AUTO_BUILD": id(cmd.town, "town"); break;
    case "RESEARCH_SPELL": id(cmd.town, "town"); break;
    case "RECRUIT":
      id(cmd.town, "town"); id(cmd.creature, "creature"); int(cmd.count, "count", 1);
      break;
    case "SEND_CARAVAN":
      id(cmd.town, "town"); id(cmd.creature, "creature"); int(cmd.count, "count", 1);
      // Exactly one source. Both, or neither, is a malformed command rather
      // than an illegal one: it does not name a thing the rules can refuse.
      if ((cmd.fromTown === null) === (cmd.fromObject === null)) {
        issues.push("exactly one of fromTown and fromObject must be set");
      }
      if (cmd.fromTown !== null) id(cmd.fromTown, "fromTown");
      if (cmd.fromObject !== null) id(cmd.fromObject, "fromObject");
      break;
    case "HIRE_HERO": id(cmd.town, "town"); id(cmd.hero, "hero"); break;
    case "UPGRADE_UNITS":
      id(cmd.town, "town"); id(cmd.army, "army"); slot(cmd.slot, "slot");
      break;
    case "SPLIT_STACK":
      id(cmd.army, "army"); slot(cmd.from, "from"); slot(cmd.to, "to"); int(cmd.count, "count", 1);
      break;
    case "MERGE_STACK": id(cmd.army, "army"); slot(cmd.from, "from"); slot(cmd.to, "to"); break;
    case "SWAP_STACK": id(cmd.army, "army"); slot(cmd.a, "a"); slot(cmd.b, "b"); break;
    case "TRANSFER_STACK":
      id(cmd.fromArmy, "fromArmy"); id(cmd.toArmy, "toArmy");
      slot(cmd.fromSlot, "fromSlot"); slot(cmd.toSlot, "toSlot"); int(cmd.count, "count", 1);
      break;
    case "DISMISS_STACK": id(cmd.army, "army"); slot(cmd.slot, "slot"); break;
    case "EQUIP_ARTIFACT":
      id(cmd.hero, "hero"); int(cmd.backpackIndex, "backpackIndex"); id(cmd.slot, "slot");
      break;
    case "UNEQUIP_ARTIFACT": id(cmd.hero, "hero"); id(cmd.slot, "slot"); break;
    case "GIVE_ARTIFACT":
      id(cmd.fromHero, "fromHero"); id(cmd.toHero, "toHero");
      int(cmd.backpackIndex, "backpackIndex");
      break;
    case "ASSEMBLE_ARTIFACT": id(cmd.hero, "hero"); id(cmd.artifact, "artifact"); break;
    case "DISASSEMBLE_ARTIFACT": id(cmd.hero, "hero"); id(cmd.slot, "slot"); break;
    case "CAST_ADVENTURE_SPELL":
      id(cmd.hero, "hero"); id(cmd.spell, "spell");
      int(cmd.x, "x"); int(cmd.y, "y"); int(cmd.z, "z");
      break;
    case "VISIT_OBJECT": id(cmd.hero, "hero"); id(cmd.object, "object"); break;
    case "TRADE_RESOURCE":
      id(cmd.player, "player"); int(cmd.amount, "amount", 1);
      if (!RESOURCES.includes(cmd.give)) issues.push("give must be a resource");
      if (!RESOURCES.includes(cmd.take)) issues.push("take must be a resource");
      break;
    case "BUY_ARTIFACT": id(cmd.hero, "hero"); id(cmd.object, "object"); id(cmd.artifact, "artifact"); break;
    case "TOWN_BUY_ARTIFACT": id(cmd.hero, "hero"); id(cmd.town, "town"); id(cmd.artifact, "artifact"); break;
    case "DIG": id(cmd.hero, "hero"); break;
    case "DISMISS_HERO": id(cmd.hero, "hero"); break;
    case "RESPOND_TO_INTERACTION":
      id(cmd.id, "id");
      if (typeof cmd.response !== "object" || cmd.response === null) issues.push("response required");
      break;
    case "BATTLE":
      if (typeof cmd.cmd !== "object" || cmd.cmd === null) issues.push("cmd required");
      break;
  }
  return issues;
}

// -------------------------------------------------------------- turn gate

/** Which player a command speaks for; null when it is not player-scoped. */
function actor(s: GameState, cmd: GameCommand): PlayerId | null {
  switch (cmd.type) {
    case "END_TURN": return cmd.player;
    case "TRADE_RESOURCE": return cmd.player;
    case "RESPOND_TO_INTERACTION": return s.pending?.playerId ?? null;
    case "MOVE_HERO": case "EQUIP_ARTIFACT": case "UNEQUIP_ARTIFACT":
    case "ASSEMBLE_ARTIFACT": case "DISASSEMBLE_ARTIFACT":
    case "CAST_ADVENTURE_SPELL": case "VISIT_OBJECT": case "BUY_ARTIFACT":
    case "TOWN_BUY_ARTIFACT": case "DISMISS_HERO": case "HERO_TO_TOWN": case "DIG":
      return s.heroes[cmd.hero]?.owner ?? null;
    case "BUILD": case "TOGGLE_AUTO_BUILD": case "RESEARCH_SPELL": case "RECRUIT": case "UPGRADE_UNITS":
      return s.towns[cmd.town]?.owner ?? null;
    // The destination's owner, which `validateCaravan` then insists is also the
    // source's: a column is dispatched BY the kingdom that will receive it.
    case "SEND_CARAVAN": return s.towns[cmd.town]?.owner ?? null;
    case "HIRE_HERO": return s.towns[cmd.town]?.owner ?? null;
    case "SPLIT_STACK": case "MERGE_STACK": case "SWAP_STACK": case "DISMISS_STACK":
      return s.armies[cmd.army]?.owner ?? null;
    case "TRANSFER_STACK": return s.armies[cmd.fromArmy]?.owner ?? null;
    case "GIVE_ARTIFACT": return s.heroes[cmd.fromHero]?.owner ?? null;
    case "BATTLE": return null;
  }
}

function gate(s: GameState, cmd: GameCommand): Reason | null {
  if (s.victory.over) return { code: "GAME_OVER" };

  if (s.pending !== null && cmd.type !== "RESPOND_TO_INTERACTION") {
    return { code: "PENDING_INTERACTION", id: s.pending.id };
  }
  if (cmd.type === "RESPOND_TO_INTERACTION") {
    if (s.pending === null) return { code: "TARGET_INVALID" };
    if (s.pending.id !== cmd.id) return { code: "PENDING_INTERACTION", id: s.pending.id };
    return null;
  }
  if (s.battle !== null && cmd.type !== "BATTLE") return { code: "BATTLE_IN_PROGRESS" };
  if (s.battle === null && cmd.type === "BATTLE") return { code: "NO_BATTLE_IN_PROGRESS" };
  if (cmd.type === "BATTLE") return null;

  const who = actor(s, cmd);
  if (who === null) return null;
  if (who !== s.turn.activePlayer) return { code: "NOT_YOUR_TURN" };
  return null;
}

// -------------------------------------------------------------------- main

export function executeCommand(
  state: GameState, command: GameCommand, content: ContentRegistry, opts?: ExecuteOpts,
): Result<CommandResult<GameState>, ExecuteError> {
  const issues = structural(command);
  if (issues.length > 0) return Err(malformed(...issues));

  const blocked = gate(state, command);
  if (blocked !== null) return Err(illegal(blocked));

  const draft = deepClone(state);
  const c = makeCtx(draft, content);

  const r = dispatch(c, command);
  if (!r.ok) return r;

  // A command that granted experience may owe levels; settling here rather than
  // at each grant site keeps the prompt out of the middle of a movement loop.
  // Not while a battle is open, though: a hero can take experience on one step
  // of a walk and meet an enemy hero on the next, and a level-up prompt raised
  // over a live battle violates "a prompt and a battle are both open" - which
  // is an invariant, so it threw rather than refusing. The levels are not lost:
  // `resolveBattleOutcome` clears `s.battle` before it grants the winner's
  // experience, so the command that ends the battle settles both debts at once.
  if (c.s.battle === null) settleAllLevelUps(c);
  evaluateVictory(c);

  if (opts?.dev !== false) {
    const bad = checkInvariants(c.s as GameState);
    if (bad !== null) throw new Error(`adventure invariant violated: ${bad}`);
  }

  c.s.version = state.version + 1;
  const out = c.s as GameState;
  if (opts?.verify === true) {
    return Ok({ state: out, events: c.events, hash: hashState(out) });
  }
  return Ok({ state: out, events: c.events });
}

type Fail = Result<never, ExecuteError>;
const no = (reason: Reason): Fail => Err(illegal(reason));
const yes: Result<true, ExecuteError> = Ok(true);

function dispatch(c: Ctx, cmd: GameCommand): Result<true, ExecuteError> {
  switch (cmd.type) {
    case "END_TURN": endTurn(c, cmd.player); return yes;
    case "MOVE_HERO": return moveHero(c, cmd.hero, cmd.path);
    case "HERO_TO_TOWN": return heroToTown(c, cmd.hero, cmd.town);
    case "BUILD": return build(c, cmd.town, cmd.building);
    case "TOGGLE_AUTO_BUILD": return toggleAutoBuild(c, cmd.town);
    case "RESEARCH_SPELL": return researchSpell(c, cmd.town, cmd.level, cmd.pick);
    case "RECRUIT": return recruit(c, cmd.town, cmd.creature, cmd.count);
    case "SEND_CARAVAN": {
      const r = sendCaravan(c, cmd.town, cmd.fromTown, cmd.fromObject, cmd.creature, cmd.count);
      return r.ok ? yes : no(r.error);
    }
    case "HIRE_HERO": return hireHero(c, cmd.town, cmd.hero);
    case "UPGRADE_UNITS": return upgradeUnits(c, cmd.town, cmd.army, cmd.slot);
    case "SPLIT_STACK": return splitStack(c, cmd.army, cmd.from, cmd.to, cmd.count);
    case "MERGE_STACK": return mergeStack(c, cmd.army, cmd.from, cmd.to);
    case "SWAP_STACK": return swapStack(c, cmd.army, cmd.a, cmd.b);
    case "TRANSFER_STACK":
      return transferStack(c, cmd.fromArmy, cmd.fromSlot, cmd.toArmy, cmd.toSlot, cmd.count);
    case "DISMISS_STACK": return dismissStack(c, cmd.army, cmd.slot);
    case "EQUIP_ARTIFACT": return equip(c, cmd.hero, cmd.backpackIndex, cmd.slot);
    case "UNEQUIP_ARTIFACT": return unequip(c, cmd.hero, cmd.slot);
    case "GIVE_ARTIFACT": return passArtifact(c, cmd.fromHero, cmd.toHero, cmd.backpackIndex);
    case "ASSEMBLE_ARTIFACT": return assembleCommand(c, cmd.hero, cmd.artifact);
    case "DISASSEMBLE_ARTIFACT": return disassembleCommand(c, cmd.hero, cmd.slot);
    case "CAST_ADVENTURE_SPELL": return castAdventure(c, cmd.hero, cmd.spell, cmd.x, cmd.y, cmd.z);
    case "VISIT_OBJECT": return visitCommand(c, cmd.hero, cmd.object);
    case "TRADE_RESOURCE": return trade(c, cmd.player, cmd.give, cmd.take, cmd.amount);
    case "BUY_ARTIFACT": return buyArtifact(c, cmd.hero, cmd.object, cmd.artifact);
    case "TOWN_BUY_ARTIFACT": return buyTownArtifact(c, cmd.hero, cmd.town, cmd.artifact);
    case "DIG": return dig(c, cmd.hero);
    case "DISMISS_HERO": return dismissHero(c, cmd.hero);
    case "RESPOND_TO_INTERACTION": return respond(c, cmd.response);
    case "BATTLE": return battle(c, cmd.cmd);
  }
}

// ------------------------------------------------------------------ movement

/** The hero's facing index, 0..7 clockwise from north (renderer contract). */
export function facingFor(dx: number, dy: number): number {
  const table: readonly (readonly number[])[] = [
    [7, 0, 1],
    [6, 4, 2],
    [5, 4, 3],
  ];
  const ix = dx < 0 ? 0 : dx > 0 ? 2 : 1;
  const iy = dy < 0 ? 0 : dy > 0 ? 2 : 1;
  return table[iy]![ix]!;
}

/**
 * Objects a hero fights *from outside*: the classic never walks a hero onto a
 * wandering monster, a live creature bank or a manned garrison. Everything else
 * is stepped onto and then visited, which is what keeps the movement loop free
 * of per-object branches.
 */
function resolveBeforeEntering(c: Ctx, o: MapObjectState): boolean {
  if (o.removed) return false;
  if (o.kind === "town" || o.kind === "hero" || o.kind === "boat") return false;
  if (o.guard === null) return o.kind === "monster" || o.kind === "bank";
  const a = c.s.armies[o.guard];
  if (a === undefined) return o.kind === "monster" || o.kind === "bank";
  return !armyEmpty(a);
}

function townAtCell(c: Ctx, x: number, y: number, z: number): TownId | null {
  for (const id of entityKeys(c.s.towns)) {
    const t = c.s.towns[id]!;
    if (t.x === x && t.y === y && t.z === z) return id as TownId;
  }
  return null;
}

/** Another hero of the mover's OWN player standing on a cell. */
function friendHeroAtCell(
  c: Ctx, x: number, y: number, z: number, mover: HeroState,
): HeroId | null {
  for (const id of entityKeys(c.s.heroes)) {
    const h = c.s.heroes[id]!;
    if (h.id === mover.id || h.owner !== mover.owner) continue;
    if (h.x === x && h.y === y && h.z === z) return id as HeroId;
  }
  return null;
}

/**
 * The enemy hero standing on a cell.
 *
 * Heroes are not map objects - nothing interns them into `map.occupant` - so
 * the occupant grid says nothing about them and `resolveBeforeEntering` never
 * sees one. A hero encounter therefore has to be looked up over the roster,
 * which is why this is a separate check in the movement loop rather than
 * another `kind` in the object branch.
 */
function enemyHeroAtCell(
  c: Ctx, x: number, y: number, z: number, mover: HeroState,
): HeroId | null {
  for (const id of entityKeys(c.s.heroes)) {
    const h = c.s.heroes[id]!;
    if (h.id === mover.id || h.owner === mover.owner) continue;
    if (h.x === x && h.y === y && h.z === z) return id as HeroId;
  }
  return null;
}

/**
 * A hero inside a town is defended by the town, so walking at him is a siege,
 * not a field battle. The siege lives on the town branch; this only has to
 * recognise the case and keep out of it.
 */
function shieldedByTown(c: Ctx, defender: HeroState): boolean {
  const townId = townAtCell(c, defender.x, defender.y, defender.z);
  if (townId === null) return false;
  return c.s.towns[townId]!.owner === defender.owner;
}

/**
 * `resumed` is the difference between "this command is illegal" and "the walk
 * is over". A prompt raised part-way along a path carries the unwalked
 * remainder in its resume token, and answering it walks that remainder - so the
 * *first* step of a resumed path is not the first step of the walk. If it is
 * blocked, or the hero spent his last movement point reaching the prompt, the
 * refusal would take the whole `RESPOND_TO_INTERACTION` with it: the draft is
 * discarded, the prompt stays open, and every enumerated response bounces the
 * same way, which deadlocks the turn. A resumed walk therefore stops where a
 * fresh one would be refused.
 */
export function moveHero(
  c: Ctx, heroId: HeroId, path: readonly number[], resumed = false,
): Result<true, ExecuteError> {
  const h0 = c.s.heroes[heroId];
  if (h0 === undefined) return no({ code: "NO_SUCH_ENTITY", id: heroId as string });
  if (path.length === 0) return yes;

  // The first step of a fresh walk is the command's own legality; the first
  // step of a resumed one is just where the hero happens to stop.
  const halt = (i: number, why: Reason): Result<true, ExecuteError> =>
    i === 0 && !resumed ? no(why) : stopHere(c, heroId);

  const map = c.s.map;
  for (let i = 0; i < path.length; i++) {
    const h = c.s.heroes[heroId]!;
    const target = path[i]!;
    if (target < 0 || target >= map.width * map.height * map.levels) {
      return halt(i, { code: "PATH_BLOCKED", atIndex: i });
    }
    const from = indexOf(map, h.x, h.y, h.z);
    const [tx, ty, tz] = cellCoords(map, target);
    const dx = tx - h.x, dy = ty - h.y;
    if (tz !== h.z || dx === 0 && dy === 0 || Math.abs(dx) > 1 || Math.abs(dy) > 1) {
      return halt(i, { code: "PATH_BLOCKED", atIndex: i });
    }

    const rules = moveRulesFor(c, h);
    if (!cellEnterable(map, target, rules)) {
      return halt(i, { code: "PATH_BLOCKED", atIndex: i });
    }

    // Asked before the occupant is looked at: a monster or a bank is joined
    // from the tile the hero stands on and costs no step, so without this a
    // hero on zero movement could still open a fight on anything adjacent.
    if (h.movementLeft <= 0) {
      return halt(i, {
        code: "INSUFFICIENT_MOVEMENT", have: 0,
        need: stepCost(map, from, target, rules, dx !== 0 && dy !== 0),
      });
    }

    const occId = occupantAt(map, target);
    const occ = occId === null ? null : c.s.objects[occId] ?? null;
    if (occ !== null && !occ.removed) {
      const blockedBy = entryIllegality(c, heroId, occ);
      if (blockedBy !== null) {
        return halt(i, blockedBy);
      }
      if (resolveBeforeEntering(c, occ)) {
        // The fight happens from the tile the hero already stands on.
        const out = visitObject(c, heroId, occ.id, { path: path.slice(i + 1), moved: true });
        if (out.kind === "battle") { startObjectBattle(c, heroId, out.object); return yes; }
        return stopHere(c, heroId);
      }
    }

    // Hero versus hero. The fight is joined from the tile the mover already
    // stands on, exactly as a wandering monster's is, and the winner never
    // steps onto the loser's cell - The classic leaves the victor where he attacked
    // from. A hero sheltering in his own town is the siege case and belongs to
    // the town branch, so this falls through rather than opening a field
    // battle inside a fort.
    const foe = enemyHeroAtCell(c, tx, ty, tz, h);
    if (foe !== null && !shieldedByTown(c, c.s.heroes[foe]!)) {
      startHeroBattle(c, heroId, foe);
      return yes;
    }

    // Hero meets hero. Two of one player's heroes never share a cell - they
    // used to, because heroes are not map occupants and so nothing along this
    // path had any reason to notice one - and in the classic walking onto your own hero
    // is not a step at all but the meeting. The mover stops on the tile he
    // already holds and `HeroesMet` says who met whom; the host opens the
    // exchange on that. `stopHere` rather than `halt`, deliberately: `halt` at
    // the first step refuses the command, and a refused command takes its
    // events down with the discarded draft, so the meeting would never be
    // reported for the one case that matters most - the hero standing right
    // beside the one he is walking into.
    const friend = friendHeroAtCell(c, tx, ty, tz, h);
    if (friend !== null) {
      c.events.push({ type: "HeroesMet", hero: heroId, other: friend });
      return stopHere(c, heroId);
    }

    const diagonal = dx !== 0 && dy !== 0;
    const cost = stepCost(map, from, target, rules, diagonal);
    // The classic's last-step rule: any remaining movement buys one more step,
    // however expensive, and leaves the hero on zero.
    const spend = cost > h.movementLeft ? h.movementLeft : cost;

    const leavingTown = h.inTown;
    if (leavingTown !== null) {
      const t = c.s.towns[leavingTown];
      if (t !== undefined && t.visitingHero === heroId) {
        patchTown(c, leavingTown, { visitingHero: null });
      }
    }

    patchHero(c, heroId, {
      x: tx, y: ty, z: tz,
      facing: facingFor(dx, dy),
      movementLeft: h.movementLeft - spend,
      inTown: null,
    });
    c.events.push({
      type: "HeroMoved", hero: heroId,
      from: [h.x, h.y, h.z], to: [tx, ty, tz], cost: spend,
    });
    revealStep(c, c.s.heroes[heroId]!);

    // The hull goes where the hero goes. A sailing hero who has just stepped
    // onto the shore has landed: the hull stays on its last water cell and,
    // as in the classic, the day is over. For a while the hull never moved and the only
    // way off it was to walk back to the cell it was summoned on.
    let landed = false;
    if (h.boat !== null) {
      if (rules.mode === "sea" && !isWaterCell(map, target)) {
        patchHero(c, heroId, { boat: null, movementLeft: 0 });
        landed = true;
      } else {
        moveObject(c, h.boat, tx, ty, tz);
      }
    }

    const town = townAtCell(c, tx, ty, tz);
    if (town !== null) {
      const t = c.s.towns[town]!;
      if (t.owner !== h.owner) {
        // A town that is not yours is taken, not visited. An undefended one is
        // walked into - The classic does not open a battle against nobody, and making
        // a player fight an empty screen for every unheld town costs the pace
        // the whole map is built around.
        if (townDefended(c, t)) { startTownBattle(c, heroId, town); return yes; }
        captureTown(c, town, h.owner);
        enterTown(c, heroId, town);
        return stopHere(c, heroId);
      }
      if (t.visitingHero === null) {
        patchTown(c, town, { visitingHero: heroId });
        patchHero(c, heroId, { inTown: town });
        arriveInTown(c, heroId, town);
      }
    }

    if (occ !== null && !occ.removed) {
      const out = visitObject(c, heroId, occ.id, { path: path.slice(i + 1), moved: true });
      if (out.kind === "battle") { startObjectBattle(c, heroId, out.object); return yes; }
      if (out.kind !== "done") return stopHere(c, heroId);
    }
    if (landed) return stopHere(c, heroId);
  }
  return stopHere(c, heroId);
}

/** A hero standing on a town he owns walks in and reads its mage guild. */
function enterTown(c: Ctx, heroId: HeroId, townId: TownId): void {
  const h = c.s.heroes[heroId];
  const t = c.s.towns[townId];
  if (h === undefined || t === undefined) return;
  if (t.owner !== h.owner) return;
  if (t.x !== h.x || t.y !== h.y || t.z !== h.z) return;
  if (t.visitingHero !== null && t.visitingHero !== heroId) return;
  patchTown(c, townId, { visitingHero: heroId });
  patchHero(c, heroId, { inTown: townId });
  arriveInTown(c, heroId, townId);
}

function stopHere(c: Ctx, heroId: HeroId): Result<true, ExecuteError> {
  scholarExchange(c, heroId);
  const h = c.s.heroes[heroId];
  if (h !== undefined) {
    c.events.push({ type: "HeroMovementSpent", hero: heroId, left: h.movementLeft });
    refreshMemory(c, h.owner);
  }
  return yes;
}

// -------------------------------------------------------------- interaction

function respond(c: Ctx, response: InteractionResponse): Result<true, ExecuteError> {
  const pending = c.s.pending;
  if (pending === null) return no({ code: "TARGET_INVALID" });
  const legal = pending.legalResponses.some((r) =>
    r.kind === response.kind &&
    (r.kind !== "OPTION" || response.kind !== "OPTION" || r.index === response.index));
  if (!legal) return no({ code: "TARGET_INVALID" });

  const token = pending.resumeToken;
  closePending(c);

  switch (token.kind) {
    case "OBJECT": return resumeObject(c, token, response);
    case "LEVEL_UP": return resumeLevelUp(c, token, response);
    case "SUBSKILL": return resumeSubskill(c, token, response);
    case "MOVE": {
      const heroId = tokenStr(token, "hero") as HeroId;
      return moveHero(c, heroId, tokenPath(token), true);
    }
    default: return yes;
  }
}

function resumeObject(
  c: Ctx, token: ResumeToken, response: InteractionResponse,
): Result<true, ExecuteError> {
  const heroId = tokenStr(token, "hero") as HeroId;
  const objectId = tokenStr(token, "object") as ObjectId;
  const path = tokenPath(token);
  const sub = tokenStr(token, "sub");

  switch (sub) {
    case "FIGHT_OR_FLEE": {
      if (response.kind !== "OK") return yes;   // declined: the hero stays put
      startObjectBattle(c, heroId, objectId);
      return yes;
    }
    case "TREASURE": {
      const wantExp = response.kind === "OPTION" && response.index === 1;
      resolveTreasureChoice(c, heroId, objectId, wantExp);
      if (settleLevelUps(c, heroId, { kind: "MOVE", data: { hero: heroId, path: [...path] } })) {
        return yes;
      }
      return path.length > 0 ? moveHero(c, heroId, path, true) : yes;
    }
    case "PORTAL": {
      if (response.kind !== "OPTION") return yes;
      const o = c.s.objects[objectId];
      if (o === undefined) return yes;
      const exits = portalExits(c, objectId, payStr(payloadOf(c, o), "channel"));
      const pick = exits[response.index];
      if (pick !== undefined) {
        teleportTo(c, heroId, pick);
        const h = c.s.heroes[heroId];
        if (h !== undefined) revealStep(c, h);
      }
      return yes;
    }
    default: {
      // Every visitable service that asks a question parks its answer here:
      // `respond` is the only door back into the kernel, and a sub-kind
      // `resumeService` does not recognise leaves the walk exactly as it was.
      resumeService(c, heroId, objectId, sub, response);
      return path.length > 0 ? moveHero(c, heroId, path, true) : yes;
    }
  }
}

/**
 * The answer to a subskill branch.
 *
 * Nothing is re-derived from the answer but the index: the token carries the
 * choices that were shown, so a pick cannot land on a different branch than the
 * one the player was reading if the hero gained another level in between. The
 * tail is `settleLevelUps` rather than `settleSubskillPicks` because a hero can
 * owe both at once - a treasure chest that pays three levels opens skills and
 * branches together - and levels are the outer debt.
 */
function resumeSubskill(
  c: Ctx, token: ResumeToken, response: InteractionResponse,
): Result<true, ExecuteError> {
  const heroId = tokenStr(token, "hero") as HeroId;
  const h = c.s.heroes[heroId];
  if (h === undefined) return yes;
  const skill = asSkillDefId(tokenStr(token, "skill"));
  const choices = tokenStr(token, "choices").split(",").filter((x) => x.length > 0);
  if (response.kind === "OPTION") {
    const pick = choices[response.index];
    if (pick !== undefined) pickSubskill(c, h, skill, asSubskillDefId(pick));
  }
  const path = tokenPath(token);
  const carry: ResumeToken = { kind: "MOVE", data: { hero: heroId, path: [...path] } };
  if (settleLevelUps(c, heroId, carry)) return yes;
  return path.length > 0 ? moveHero(c, heroId, path, true) : yes;
}

function resumeLevelUp(
  c: Ctx, token: ResumeToken, response: InteractionResponse,
): Result<true, ExecuteError> {
  const heroId = tokenStr(token, "hero") as HeroId;
  const h = c.s.heroes[heroId];
  if (h === undefined) return yes;
  const offers = tokenStr(token, "offers").split(",").filter((s) => s.length > 0);
  if (response.kind === "OPTION") {
    const pick = offers[response.index];
    if (pick !== undefined) learnSkill(c, h, asSkillDefId(pick));
  }
  const path = tokenPath(token);
  const carry: ResumeToken = { kind: "MOVE", data: { hero: heroId, path: [...path] } };
  if (settleLevelUps(c, heroId, carry)) return yes;
  return path.length > 0 ? moveHero(c, heroId, path, true) : yes;
}

// ------------------------------------------------------------------ battle

/**
 * Section 27 - the machines a hero wheels onto the field.
 *
 * A war machine is an artifact in one of the `warMachine*` slots and a creature
 * def on the battlefield, and the two are tied by name rather than by a table:
 * the artifact `catapult` fields the creature `warmachine.catapult`. That keeps
 * the pairing a content fact - shipping a fifth machine is a new artifact and a
 * new creature and no code - and a slot whose artifact has no creature behind
 * it simply fields nothing.
 */
const WAR_MACHINE_PREFIX = "warmachine.";

function heroMachines(c: Ctx, h: HeroState): readonly CreatureDefId[] {
  const out: CreatureDefId[] = [];
  for (const slot of equippedSlots(h)) {
    if (!slot.startsWith("warMachine")) continue;
    const id = h.artifacts[slot];
    if (id === null || id === undefined) continue;
    const def = asCreatureDefId(WAR_MACHINE_PREFIX + (id as string));
    if (!c.content.creatures.has(def) || out.includes(def)) continue;
    out.push(def);
  }
  // A hero whose specialty *is* a machine brings it whether or not he owns the
  // artifact: a ballista specialist who has to go shopping for a ballista is
  // not a specialist.
  const hd = c.content.heroes.get(h.def);
  if (hd !== undefined) {
    for (const id of specialtyMachines(c.content, hd)) if (!out.includes(id)) out.push(id);
  }
  return out;
}

/**
 * Section 26 - the artifacts whose effect is a battle rule.
 *
 * A hero's modifier list is the adventure kernel's vocabulary; the battle
 * kernel has its own. This is the one translation between them, and it is a
 * table rather than a chain of ifs so that adding an artifact effect is a data
 * edit plus one row. Three destinations:
 *
 *   army    on every stack of this hero's own army (magic resistance, the
 *           pendants' immunities, a bow that cancels the distance penalty)
 *   field   on every stack on both sides (Oathbreaker's Cloak, Chains of Battle),
 *           sourced under the magic prefix so `sideMagicMods` finds them
 *           whichever hero asks
 *   profile folded into the magic profile as a number (school damage, a
 *           mastery floor, the morale the other side loses)
 */
const SRC_FIELD = "hero.magic.field.";
/**
 * A seal rides on the WEARER's own stacks, not on the field.
 *
 * `fieldModifiers` in `battle/init.ts` gathers from both heroes and installs on
 * every stack, so field scope is symmetric by construction and cannot say
 * "only the other hero". Army scope can - and the source still has to sit under
 * `MAGIC_SRC_PREFIX`, because `sideMagicMods` collects nothing else.
 */
const SRC_SEAL = "hero.magic.seal.";
/** Runes rides on every stack of the hero's army; `battle/runes.ts` reads it. */
const SRC_RUNES = asModifierSrcId("skill.runes");
const RUNES_SKILL = asSkillDefId("runes");

/**
 * The hero's Runes level, as a modifier every stack of his army carries.
 *
 * Army rather than field: Runes is one hero's training and must not reach the
 * other side's creatures. `op: "max"` because two sources of the same skill
 * level are still that level, never twice it.
 */
function runeModifiers(level: number): readonly Modifier[] {
  return level <= 0 ? [] : [{
    source: SRC_RUNES, stat: STAT_RUNES, op: "max", value: level,
    expires: { kind: "battle" },
  }];
}

interface BattleKit {
  readonly army: readonly Modifier[];
  readonly field: readonly Modifier[];
  readonly schoolDamageFp: Record<string, number>;
  readonly masteryFloor: Record<string, number>;
  readonly moraleEnemy: number;
  readonly luckEnemy: number;
}

function battleKit(c: Ctx, h: HeroState): BattleKit {
  const army: Modifier[] = [];
  const field: Modifier[] = [];
  const schoolDamageFp: Record<string, number> = {};
  const masteryFloor: Record<string, number> = {};
  let moraleEnemy = 0;
  let luckEnemy = 0;
  let fieldSeq = 0;

  const toArmy = (m: Modifier, stat: StatKey): void => {
    army.push({ ...m, stat, expires: { kind: "battle" } });
  };
  const toField = (m: Modifier, stat: StatKey): void => {
    field.push({
      source: asModifierSrcId(`${SRC_FIELD}${fieldSeq++}`),
      stat, op: m.op, value: m.value, expires: { kind: "battle" },
    });
  };

  for (const m of heroModifiers(c, h)) {
    const stat = m.stat as string;
    // A hero's Windrunner Cape or Band of Health is worn by the army: the
    // battle resolves `speed` and `hp` per stack, so these land on every stack
    // the way Offense and Armorer do. They were authored with `expires: battle`
    // from the start and translated by nothing.
    if (stat === "speed" || stat === "hp") { toArmy(m, stat); continue; }
    // The subskill combat family. Same destination as Offense and Armorer -
    // every stack of this hero's army - but named rather than dug out of a
    // rung, because a branch has no rung of its own (`modifiers.ts`).
    if (stat === "damageMelee") { toArmy(m, STAT_DAMAGE_MELEE); continue; }
    if (stat === "damageRanged") { toArmy(m, STAT_DAMAGE_RANGED); continue; }
    if (stat === "damageDealt") { toArmy(m, STAT_DAMAGE_DEALT); continue; }
    if (stat === "damageTaken") { toArmy(m, STAT_DAMAGE_TAKEN); continue; }
    if (stat === "fireShield") { toArmy(m, STAT_FIRE_SHIELD); continue; }
    if (stat === "extraRetaliation") { toArmy(m, STAT_EXTRA_RETALIATION); continue; }
    if (stat === "regen") { toArmy(m, STAT_REGEN); continue; }
    if (stat === "runeCap") { toArmy(m, STAT_RUNE_CAP); continue; }
    if (stat === "magicResist") { toArmy(m, STAT_MAGIC_RESIST); continue; }
    if (stat === "magicMirror") { toArmy(m, STAT_MAGIC_MIRROR); continue; }
    if (stat === "rangePenaltyCut") { toArmy(m, STAT_RANGE_PENALTY_CUT); continue; }
    if (stat.startsWith("immune.")) { toArmy(m, spellImmunityStat(stat.slice(7))); continue; }
    if (stat === "spellLevelCap") { toField(m, STAT_SPELL_LEVEL_CAP); continue; }
    if (stat === "spellLevelFloor") { toField(m, STAT_SPELL_LEVEL_FLOOR); continue; }
    if (stat === "noEscape") { toField(m, STAT_NO_ESCAPE); continue; }
    if (stat === "ignoreImmunity") { toField(m, STAT_IGNORE_IMMUNITY); continue; }
    if (stat === "noRaise") { toField(m, STAT_NO_RAISE); continue; }
    if (stat.startsWith("sealSchoolEnemy.")) {
      army.push({
        source: asModifierSrcId(`${SRC_SEAL}${fieldSeq++}`),
        stat: sealedSchoolStat(stat.slice("sealSchoolEnemy.".length)),
        op: m.op, value: m.value, expires: { kind: "battle" },
      });
      continue;
    }
    if (stat.startsWith("schoolDamage.")) {
      const school = stat.slice("schoolDamage.".length);
      const cur = schoolDamageFp[school] ?? FP_ONE;
      schoolDamageFp[school] = m.op === "mulFp" ? Math.round((cur * m.value) / FP_ONE) : cur + m.value;
      continue;
    }
    if (stat.startsWith("mastery.")) {
      const school = stat.slice("mastery.".length);
      const cur = masteryFloor[school] ?? 0;
      if (m.value > cur) masteryFloor[school] = m.value;
      continue;
    }
    if (stat === "moraleEnemy") moraleEnemy += m.value;
    else if (stat === "luckEnemy") luckEnemy += m.value;
  }
  return { army, field, schoolDamageFp, masteryFloor, moraleEnemy, luckEnemy };
}

/**
 * Section 26 - the tomes. An artifact that "grants every Air spell" is not a
 * one-off gift: taking the tome off takes the spells back, so the book a hero
 * casts from is his own plus whatever his kit is lending him, computed on every
 * read rather than written into `spellbook`.
 */
export function spellbookOf(c: Ctx, h: HeroState): readonly SpellDefId[] {
  const lent: string[] = [];
  const schools: string[] = [];
  const levels: number[] = [];
  for (const m of heroModifiers(c, h)) {
    const stat = m.stat as string;
    if (m.value <= 0) continue;
    if (stat.startsWith("teachSchool.")) schools.push(stat.slice("teachSchool.".length));
    else if (stat.startsWith("teachLevel.")) levels.push(Number(stat.slice("teachLevel.".length)));
  }
  const held = new Set<string>(h.spellbook.map((x) => x as string));

  /**
   * Spells one named artifact carries, as opposed to a whole school a tome
   * lends. Colossus Storm's lightning bolt is the first: `grantsSpells` names
   * it, and nothing else in the game can put it in a book - it is
   * `artifactOnly`, so no guild, no Shrine, no research and no Scholar reaches
   * it, and neither does the loop below.
   *
   * Read off the equipped slots and not the backpack, for the same reason every
   * other artifact effect is: a relic in the pack is luggage.
   */
  for (const slot of Object.keys(h.artifacts).sort()) {
    const worn = h.artifacts[slot];
    if (worn === null || worn === undefined) continue;
    const art = c.content.artifacts.get(worn);
    for (const id of art?.grantsSpells ?? []) {
      if (held.has(id as string) || lent.includes(id as string)) continue;
      if (c.content.spells.get(id) === undefined) continue;
      lent.push(id as string);
    }
  }

  if (schools.length > 0 || levels.length > 0) {
    for (const id of [...c.content.spells.keys()].sort()) {
      if (held.has(id as string) || lent.includes(id as string)) continue;
      const def = c.content.spells.get(id)!;
      // A tome lends a school; it does not lend what only a relic carries.
      if (def.artifactOnly === true) continue;
      if (schools.includes(def.school) || levels.includes(def.level)) lent.push(id as string);
    }
  }

  if (lent.length === 0) return h.spellbook;
  return [...h.spellbook, ...lent.map(asSpellDefId)].sort();
}

function heroBattleInput(c: Ctx, h: HeroState): BattleHeroInput {
  const kit = battleKit(c, h);
  // Through `primaryFor`, not off `h.primary`: a hero's Attack is his own plus
  // whatever his artifacts, his specialty and his kingdom's towns add to it,
  // and reading the raw field is what made a Glade Axe worth nothing.
  const primary = primaryFor(c, h);
  return {
    id: h.id,
    owner: h.owner,
    attack: primary.attack,
    defense: primary.defense,
    spellPower: primary.spellPower,
    knowledge: primary.knowledge,
    mana: h.mana,
    spellbook: [...spellbookOf(c, h)],
    army: h.army,
    morale: statFor(c, h, "morale", 0).value,
    luck: statFor(c, h, "luck", 0).value,
    offenseFp: skillDamageFp(c, h, "offense"),
    archeryFp: skillDamageFp(c, h, "archery"),
    armorerFp: skillDamageFp(c, h, "armorer"),
    meleeDamageFp: 0,
    rangedDamageFp: 0,
    wisdom: skillLevelOf(c, h, CODED_SKILLS.wisdom),
    mastery: {
      air: masteryOf(c, h, "air", kit),
      earth: masteryOf(c, h, "earth", kit),
      fire: masteryOf(c, h, "fire", kit),
      water: masteryOf(c, h, "water", kit),
    },
    sorceryFp: statFor(c, h, "spellDamage", FP_ONE).value,
    spellCostDelta: statFor(c, h, "spellCost", 0).value,
    spellDurationBonus: statFor(c, h, "spellDuration", 0).value,
    machines: heroMachines(c, h),
    ballistics: machineLevel(c, h, MACHINE_SKILLS.ballistics, "ballistics"),
    artillery: machineLevel(c, h, MACHINE_SKILLS.artillery, "artillery"),
    firstAid: machineLevel(c, h, MACHINE_SKILLS.firstAid, "firstAid"),
    // The hero's own specialty, plus whatever his towns are granting his
    // creatures - the Guiding Star and its kin. One list, one install path.
    creatureModifiers: [
      ...specialtyCreatures(c, h),
      ...(h.owner === null ? [] : kingdomCreatureBonuses(c, h.owner)),
    ],
    spellBonusFp: specialtySpells(c, h),
    schoolDamageFp: kit.schoolDamageFp,
    armyModifiers: [...kit.army, ...runeModifiers(skillLevelOf(c, h, RUNES_SKILL))],
    fieldModifiers: kit.field,
    moraleEnemy: kit.moraleEnemy,
    luckEnemy: kit.luckEnemy,
    spellPowerEnemyFp: statFor(c, h, "spellPowerEnemy", FP_ONE).value,
  };
}

/** School mastery: the skill the hero learned, or the orb he is carrying. */
function masteryOf(
  c: Ctx, h: HeroState, school: "air" | "earth" | "fire" | "water", kit: BattleKit,
): number {
  const learned = skillLevelOf(c, h, MAGIC_SCHOOL_SKILLS[school]);
  const worn = kit.masteryFloor[school] ?? 0;
  return worn > learned ? worn : learned;
}

/** The hero's creature (or war-machine) specialty, expanded per creature def. */
function specialtyCreatures(c: Ctx, h: HeroState): readonly CreatureModifiers[] {
  const hd = c.content.heroes.get(h.def);
  return hd === undefined ? [] : creatureBonusesFor(c.content, hd, h.level);
}

function specialtySpells(c: Ctx, h: HeroState): Readonly<Record<string, number>> {
  const hd = c.content.heroes.get(h.def);
  return hd === undefined ? {} : spellSpecialtyFp(hd, h.level);
}

/**
 * Section 27 - the tactics phase.
 *
 * The classic does not give both armies a tactics phase. The side with the better
 * Tactics rearranges and the other watches, over the *difference* between the
 * two, which is why the battle kernel carries one signed number: sign says who
 * won the contest, magnitude says by how much (`BattleState.tacticsHexes`).
 * Two experts cancel out and nobody gets a phase, which is the rule.
 */
const TACTICS_SKILL = asSkillDefId("tactics");
export const TACTICS_HEXES: readonly number[] = Object.freeze([0, 3, 5, 7]);

function tacticsWidth(c: Ctx, h: HeroState | null): number {
  if (h === null) return 0;
  const taught = TACTICS_HEXES[skillLevelOf(c, h, TACTICS_SKILL)] ?? 0;
  // Widened by the kit, but only for a hero who has the skill at all: a band
  // granted to someone who never learned Tactics would hand a phase to a hero
  // the contest is supposed to be between two trained ones.
  if (taught === 0) return 0;
  return taught + statFor(c, h, "tacticsHexes", 0).value;
}

/**
 * The signed band for one battle. A siege has none: the defender is behind a
 * wall he cannot be marched away from and the attacker's approach is the whole
 * problem the siege exists to pose, so the classic turns the phase off entirely rather
 * than letting Tactics teleport a stack past the moat.
 */
function tacticsHexesFor(
  c: Ctx, attacker: HeroState | null, defender: HeroState | null, siege: boolean,
  field?: { readonly cavern: boolean; readonly ambush: boolean },
): number {
  if (siege) return 0;
  const band = tacticsWidth(c, attacker) - tacticsWidth(c, defender);
  if (band !== 0) return band;
  if (field === undefined || (!field.cavern && !field.ambush)) return 0;
  const extra = (h: HeroState | null): number => {
    if (h === null || !armyIsFactionPure(c, h)) return 0;
    return c.content.factions.get(h.faction)?.blindsideHexes ?? 0;
  };
  const atk = extra(attacker);
  if (atk > 0) return atk;
  const def = extra(defender);
  if (def > 0) return -def;
  return 0;
}

function fieldBlindside(
  c: Ctx, attacker: HeroState, x: number, y: number, z: number,
): { readonly cavern: boolean; readonly ambush: boolean } {
  const i = indexOf(c.s.map, x, y, z);
  const kind = i < 0 ? "grass" : (TERRAINS[c.s.map.terrain[i]!] ?? "grass");
  const fog = c.s.fog[attacker.owner];
  const ambush = fog !== undefined && visibilityAt(fog, c.s.map, x, y, z) === FOG_UNSEEN;
  return { cavern: kind === "cavern", ambush };
}

/**
 * Section 27 - the three skills that crew a war machine. Which machine each one
 * commands is the battle kernel's business (`machines.ts` reads the role off the
 * creature); all the adventure kernel does is say how well the hero was taught.
 */
const MACHINE_SKILLS = Object.freeze({
  ballistics: asSkillDefId("ballistics"),
  artillery: asSkillDefId("artillery"),
  firstAid: asSkillDefId("first_aid"),
});

/** The four school skills, by the `SpellDef.school` name the content uses. */
const MAGIC_SCHOOL_SKILLS = Object.freeze({
  air: asSkillDefId("air_magic"),
  earth: asSkillDefId("earth_magic"),
  fire: asSkillDefId("fire_magic"),
  water: asSkillDefId("water_magic"),
});

/*
 * Spellcraft, the artifacts that sharpen a spell and the towns that cheapen one all
 * reach the battle kernel as `spellDamage`, `spellCost` and `spellDuration` read
 * straight off the hero (see `heroBattleInput`). It used to be Spellcraft alone,
 * dug out of the skill's own modifier; going through `statFor` costs nothing and
 * means an artifact with a `spellCost` on it is not silently inert.
 */

/**
 * How well this hero crews one machine: the skill, or a floor his kit puts
 * under it, whichever is higher.
 *
 * The `pathfinding` shape (`modifiers.ts`), for the same reason: the rule reads
 * a level, and a subskill has no level of its own to be read from. Clamped to
 * the three rungs the crew tables are sized for.
 */
function machineLevel(c: Ctx, h: HeroState, skill: SkillDefId, stat: StatKey): number {
  const taught = skillLevelOf(c, h, skill);
  if (taught === 0) return 0;
  return clampInt(Math.max(taught, statFor(c, h, stat, 0).value), 0, 3);
}

/** Fixed point, 100 meaning 10% - the battle kernel's convention. */
function skillDamageFp(c: Ctx, h: HeroState, name: string): number {
  const id = asSkillDefId(name);
  const lvl = skillLevelOf(c, h, id);
  if (lvl === 0) return 0;
  const hd = c.content.heroes.get(h.def);
  const def = c.content.skills.get(id);
  const rung = def?.levels[lvl - 1] ?? [];
  let base = 0;
  for (const m of rung) {
    if (m.op === "mulFp" && (m.stat === "damageMin" || m.stat === "damageMax")) {
      base = m.value - FP_ONE;
      break;
    }
  }
  if (base === 0) base = (SKILL_PCT_FALLBACK[name]?.[lvl] ?? 0) * 10;
  if (base === 0) return 0;
  // A hero specialised in this skill amplifies it on top: the specialty's second
  // copy of the rung's own modifiers (`skillModifiersFor`) names `damageMin` /
  // `damageMax`, which `battleKit` never reads, so without this the number-skill
  // specialty leg wrote modifiers nobody ever applied.
  if (hd !== undefined && hd.specialty.kind === "skill" && hd.specialty.target === (id as string)) {
    for (const m of skillModifiersFor(c.content, hd, h, h.level)) {
      if (m.stat === "damageMin" || m.stat === "damageMax") base += m.value - FP_ONE;
    }
  }
  return base;
}

function battlefieldFor(c: Ctx, x: number, y: number, z: number): string {
  const i = indexOf(c.s.map, x, y, z);
  if (i < 0) return "grass";
  // The kind, not the art path. `BattleState.terrain` is what the renderer
  // looks up in `battlefield.*` and what Cinders/Canopy/Miasma match on.
  // `TerrainDef.battlefield` is a file path (`bf_lava.png`); handing that
  // through used to miss the atlas and fall back to grass on every non-grass
  // fight.
  return TERRAINS[c.s.map.terrain[i]!] ?? "grass";
}

/**
 * Section 31 - the magic ground a fight at this cell is fought on, or null.
 *
 * Read off the DEFENDER's cell, which is the cell the battle is at: a hero
 * charging out of a clover field onto cursed ground fights on the cursed
 * ground, the same way the terrain under the defender decides the arena.
 */
function groundAt(c: Ctx, x: number, y: number, z: number): MagicGroundDef | undefined {
  const i = indexOf(c.s.map, x, y, z);
  if (i < 0) return undefined;
  const slot = c.s.map.ground[i] ?? 0;
  if (slot === 0) return undefined;
  const id = c.s.map.groundIndex[slot];
  // A map whose legend names a ground this bundle no longer ships is ordinary
  // ground, not a crash: content moves and old maps do not.
  return id === null || id === undefined ? undefined : c.content.magicGrounds.get(id);
}

/** The arena a fight here is painted on: the magic ground, else the terrain. */
function arenaFor(c: Ctx, x: number, y: number, z: number): string {
  return groundAt(c, x, y, z)?.battlefield ?? battlefieldFor(c, x, y, z);
}

function wetKindAt(c: Ctx, x: number, y: number, z: number, onBoat: boolean): ReturnType<typeof wetKindFromTerrain> {
  const i = indexOf(c.s.map, x, y, z);
  if (i < 0) return onBoat ? "water" : "none";
  const kind = TERRAINS[c.s.map.terrain[i]!] ?? "grass";
  return wetKindFromTerrain(kind, c.s.map.river[i]! !== 0, onBoat);
}

function battleRng(c: Ctx): Record<string, RngStreamState> {
  const out: Record<string, RngStreamState> = {};
  for (const k of RNG_STREAMS) if (k.startsWith("battle.")) out[k] = c.s.rng[k]!;
  return out;
}

/**
 * An open field, dressed.
 *
 * A siege gets its board from `siegeObstacles`; a field fight has no fort to
 * derive one from, so the props are rolled off the battle's own streams here
 * and the advanced stream goes into the battle with them. Every open-field
 * opening in this file goes through this, which is why an unwalled town is
 * fought over the same ground a wandering guard is: `siegeObstacles(0)` is the
 * empty list, and until this existed that meant a bare board.
 */
function fieldSetup(c: Ctx): { obstacles: readonly Obstacle[]; rng: Record<string, RngStreamState> } {
  const rng = battleRng(c);
  const seed = rng[FIELD_OBSTACLE_STREAM];
  if (seed === undefined) return { obstacles: [], rng };
  const [obstacles, next] = fieldObstacles(seed);
  rng[FIELD_OBSTACLE_STREAM] = next;
  return { obstacles, rng };
}

/** Hand the advanced battle streams back so the adventure sequence continues. */
function absorbBattleRng(c: Ctx): void {
  const b = c.s.battle;
  if (b === null) return;
  const rng: Record<string, RngStreamState> = { ...c.s.rng };
  for (const k of RNG_STREAMS) {
    if (!k.startsWith("battle.")) continue;
    const v = b.rng[k];
    if (v !== undefined) rng[k] = v;
  }
  c.s.rng = rng as GameState["rng"];
}

export function startObjectBattle(c: Ctx, heroId: HeroId, objectId: ObjectId): void {
  const h = c.s.heroes[heroId];
  const o = c.s.objects[objectId];
  if (h === undefined || o === undefined || o.guard === null) return;
  const guard = c.s.armies[o.guard];
  const attacker = c.s.armies[h.army];
  if (guard === undefined || attacker === undefined) return;

  const field = fieldSetup(c);
  // An anti-magic garrison silences BOTH sides: a spell-level floor of five
  // refuses every spell there is, installed the way a Hush Cape is.
  const silence: Modifier[] = payFlag(payloadOf(c, o), "antiMagic")
    ? [{
      source: asModifierSrcId(`${MAGIC_SRC_PREFIX}garrison`), stat: STAT_SPELL_LEVEL_FLOOR,
      op: "max", value: 5, expires: { kind: "battle" },
    }]
    : [];
  const attackerHero = heroBattleInput(c, h);
  c.s.battle = startBattle({
    attacker: {
      slots: attacker.slots,
      hero: { ...attackerHero, fieldModifiers: [...(attackerHero.fieldModifiers ?? []), ...silence] },
    },
    defender: {
      slots: guard.slots,
      hero: {
        id: null, owner: null, attack: 0, defense: 0, spellPower: 0, knowledge: 0,
        mana: 0, spellbook: [], army: o.guard, fieldModifiers: silence,
      },
    },
    terrain: arenaFor(c, o.x, o.y, o.z),
    ground: groundAt(c, o.x, o.y, o.z),
    wetKind: wetKindAt(c, o.x, o.y, o.z, h.boat !== null),
    obstacles: field.obstacles,
    siege: null,
    rng: field.rng as never,
    tacticsHexes: tacticsHexesFor(c, h, null, false, fieldBlindside(c, h, o.x, o.y, o.z)),
  }, c.content);
  for (const e of openBattle(c.s.battle, c.content)) c.events.push(e);
  absorbBattleRng(c);
}

/**
 * Two heroes and their armies, on the defender's ground.
 *
 * Both sides get a full `BattleHeroInput`, not just the attacker's: the battle
 * kernel holds no `HeroState`, so a profile that is not handed across simply
 * does not exist inside the fight, and a defender passed as the empty guard
 * profile would be a hero who cannot cast, has no morale and no Armorer.
 */
export function startHeroBattle(c: Ctx, attackerId: HeroId, defenderId: HeroId): void {
  const a = c.s.heroes[attackerId];
  const d = c.s.heroes[defenderId];
  if (a === undefined || d === undefined || a.owner === d.owner) return;
  const aArmy = c.s.armies[a.army];
  const dArmy = c.s.armies[d.army];
  if (aArmy === undefined || dArmy === undefined) return;

  const field = fieldSetup(c);
  c.s.battle = startBattle({
    attacker: { slots: aArmy.slots, hero: heroBattleInput(c, a) },
    defender: { slots: dArmy.slots, hero: heroBattleInput(c, d) },
    terrain: arenaFor(c, d.x, d.y, d.z),
    ground: groundAt(c, d.x, d.y, d.z),
    wetKind: wetKindAt(c, d.x, d.y, d.z, a.boat !== null || d.boat !== null),
    obstacles: field.obstacles,
    siege: null,
    rng: field.rng as never,
    tacticsHexes: tacticsHexesFor(c, a, d, false, fieldBlindside(c, a, d.x, d.y, d.z)),
  }, c.content);
  for (const e of openBattle(c.s.battle, c.content)) c.events.push(e);
  absorbBattleRng(c);
  // A hero who has given every stack away is beaten the moment the battle
  // opens, and there is no command left to fold that outcome back - so it is
  // folded here. A guard army can never be empty (`resolveBeforeEntering`
  // refuses to start that fight), which is why only this path needs it.
  if (c.s.battle !== null && c.s.battle.outcome.kind !== "ongoing") resolveBattleOutcome(c);
}

// --------------------------------------------------------------------- siege

/**
 * Section 12 - what the town's works are worth to whoever is holding it.
 *
 * A Tavern's morale, a Order of the Blade, a Lucky Fountain and a
 * Blood Stone are all authored as battle-scoped modifiers on the building
 * (see `buildings.ts`), and they land on the *defending side* rather than on a
 * hero: a garrison with no hero at all still fights behind the walls it paid
 * for, and until now it fought with the empty profile.
 */
interface TownWorks {
  readonly morale: number;
  readonly luck: number;
  readonly attack: number;
  readonly defense: number;
}

function townDefence(c: Ctx, t: TownState, fortLevel: number): TownWorks {
  const mods = defenceModifiers(c, t);
  const of = (stat: StatKey): number =>
    resolveStat(0, mods.filter((m) => m.stat === stat)).value;
  // `defenseSiege` is defence that only exists while there is something to
  // defend: an open town's obelisk is a monument, not a defensive wall.
  const siege = fortLevel > 0 ? of("defenseSiege") : 0;
  return { morale: of("morale"), luck: of("luck"), attack: of("attack"), defense: of("defense") + siege };
}

function withWorks(h: BattleHeroInput, w: TownWorks): BattleHeroInput {
  return {
    ...h,
    attack: h.attack + w.attack,
    defense: h.defense + w.defense,
    morale: (h.morale ?? 0) + w.morale,
    luck: (h.luck ?? 0) + w.luck,
  };
}

/**
 * The town-screen slot the Fort / Citadel / Castle chain occupies. The chain's
 * depth is the fort level, so a faction that names its steps differently, or a
 * mod that adds a fourth, needs no table here.
 */
const FORT_SLOT = "fort";

/**
 * Where the works stand on the fifteen-column battlefield. The defenders deploy
 * on column 14 (and 13 for a two-hex stack), so a wall on column 11 leaves them
 * enclosed with two columns of bailey behind it, and the ditch in front of the
 * wall is column 10.
 */
export const SIEGE_WALL_COL = 11;
export const SIEGE_GATE_ROW = 5;
/** `Obstacle.integrity`: 2 intact, 1 damaged, 0 destroyed. */
const WALL_INTACT = 2;
/** Rows the turrets stand on, in the order the keep is built before them. */
const TOWER_ROWS: readonly number[] = Object.freeze([1, 10]);

/**
 * 0 for an open town, 1 Fort, 2 Citadel, 3 Castle: the length of the built
 * fort-slot building's upgrade chain. `TownState.built` keeps one entry per
 * slot, so there is at most one to find.
 */
export function fortLevelOf(c: Ctx, t: TownState): number {
  let level = 0;
  for (const id of t.built) {
    const b = c.content.buildings.get(id);
    if (b === undefined || b.slot !== FORT_SLOT) continue;
    let depth = 1;
    let up = b.upgradeOf;
    // The chain is finite and acyclic by content validation; the bound is a
    // belt-and-braces stop, not a rule.
    for (let step = 0; up !== null && step < 8; step++) {
      const prev = c.content.buildings.get(up);
      if (prev === undefined) break;
      depth += 1;
      up = prev.upgradeOf;
    }
    if (depth > level) level = depth;
  }
  return level;
}

/**
 * Turrets the fort level pays for: a Citadel raises the keep, a Castle adds a
 * flank. Each is an `Obstacle` of kind `tower` and falls on its `integrity`
 * exactly as a wall segment does.
 */
export function siegeTowerCount(fortLevel: number): number {
  if (fortLevel <= 1) return 0;
  if (fortLevel === 2) return 1;
  return 2;
}

/**
 * The fortification, as battlefield obstacles.
 *
 * A curtain wall down column 11 with a ditch in front of it, a gateway on row 5
 * and, once the fort has been upgraded, turrets standing in the wall line.
 *
 * **The gate is barred**, and the attacker has to break it: `startBattle` hands
 * the besieging hero the siege engine the classic never lets him leave at home, and the
 * engine cannot be shot at, so a gate shut in round one is a gate that comes
 * down. The battle kernel keeps the safety catch - an attacker who somehow
 * brings no breaker finds the bridge already down, because masonry no stack can
 * cross is a battle that cannot be concluded rather than a siege that is hard to
 * win. Around the gate the walls still halve every shot that crosses them, the
 * ditch still ends a move and costs blood, and the defenders still have to be
 * dug out of a one-hex funnel.
 */
export function siegeObstacles(
  fortLevel: number, extraIntegrity = 0,
): readonly Obstacle[] {
  if (fortLevel <= 0) return [];
  const intact = WALL_INTACT + (extraIntegrity > 0 ? extraIntegrity : 0);
  const out: Obstacle[] = [];
  const towers = siegeTowerCount(fortLevel);
  const towerRows = TOWER_ROWS.slice(0, towers);
  for (let row = 0; row < BF_HEIGHT; row++) {
    const hex = hexIndex(SIEGE_WALL_COL, row);
    const kind = row === SIEGE_GATE_ROW ? "gate"
      : towerRows.includes(row) ? "tower" : "wall";
    out.push({ kind, hex, integrity: intact, art: "" });
  }
  // The ditch is cut in front of the whole run. It skips the gate row, which is
  // where the bridge lands, so there is one lane in and it is covered.
  for (let row = 0; row < BF_HEIGHT; row++) {
    if (row === SIEGE_GATE_ROW) continue;
    out.push({ kind: "moat", hex: hexIndex(SIEGE_WALL_COL - 1, row), integrity: 0, art: "" });
  }
  return out;
}

// ---------------------------------------------------------------- town battle

/** Who commands a town's defence: the garrisoned hero first, else the visitor. */
export function townDefender(c: Ctx, t: TownState): HeroState | null {
  const id = t.garrisonHero ?? t.visitingHero;
  if (id === null) return null;
  return c.s.heroes[id] ?? null;
}

/**
 * Is there anything in this town to fight? A town with no garrison and no hero
 * is walked into rather than besieged, which is the classic's behaviour and is what
 * keeps an undefended flank from costing a whole day.
 */
export function townDefended(c: Ctx, t: TownState): boolean {
  const g = c.s.armies[t.garrison];
  if (g !== undefined && !armyEmpty(g)) return true;
  const hero = townDefender(c, t);
  if (hero === null) return false;
  const a = c.s.armies[hero.army];
  return a !== undefined && !armyEmpty(a);
}

/**
 * Fold the garrison into the defending hero's own army.
 *
 * A battle side is one army - `foldSide` writes casualties back by slot index -
 * so a town defended by a hero has to field one army, not two. The classic merges a
 * hero's troops with the garrison the moment he takes the garrison, and this is
 * the same merge seen from the other end: the hero leads everything the town
 * has. Creatures that do not fit in his seven slots stay behind and sit the
 * battle out.
 */
function absorbGarrison(c: Ctx, t: TownState, hero: HeroState): void {
  const g = c.s.armies[t.garrison];
  const h = c.s.armies[hero.army];
  if (g === undefined || h === undefined) return;

  // Pool both armies, merging stacks of the same creature: a garrison of
  // twenty spearwardens next to a hero's ten is thirty spearwardens and one slot, not
  // two slots and a stack left behind.
  const pooled = new Map<string, number>();
  for (const s of [...h.slots, ...g.slots]) {
    if (s === null || s.count <= 0) continue;
    pooled.set(s.def as string, (pooled.get(s.def as string) ?? 0) + s.count);
  }

  // Seven slots for what the town has: the seven worth most. Sorted by what the
  // stack is worth and then by id, so the choice is the same on every run and
  // the overflow is the part a player would have left behind anyway. Dropping
  // the tail of the list - which is what this used to do - could leave the
  // dragons in the garrison because they happened to sit in slot eight.
  const ranked = [...pooled.entries()].sort((a, b) => {
    const va = (c.content.creatures.get(asCreatureDefId(a[0]))?.aiValue ?? 0) * a[1];
    const vb = (c.content.creatures.get(asCreatureDefId(b[0]))?.aiValue ?? 0) * b[1];
    return vb - va || (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0);
  });

  const front: (ArmySlot | null)[] = emptyArmySlots();
  const rest: (ArmySlot | null)[] = emptyArmySlots();
  ranked.forEach(([def, count], i) => {
    const slot = { def: asCreatureDefId(def), count };
    if (i < ARMY_SLOTS) front[i] = slot;
    else if (i - ARMY_SLOTS < ARMY_SLOTS) rest[i - ARMY_SLOTS] = slot;
  });

  for (let i = 0; i < ARMY_SLOTS; i++) {
    setSlot(c, hero.army, i, front[i] ?? null);
    setSlot(c, t.garrison, i, rest[i] ?? null);
  }
}

/**
 * The assault on a town the moving hero does not own.
 *
 * Routed through exactly the machinery a guarded object uses: one `startBattle`,
 * one `openBattle`, and the RNG handed across in both directions, so the
 * battle-end fold recognises it and `captureTown` runs there and nowhere else.
 */
export function startTownBattle(c: Ctx, heroId: HeroId, townId: TownId): void {
  const h = c.s.heroes[heroId];
  const t0 = c.s.towns[townId];
  if (h === undefined || t0 === undefined) return;
  const attacker = c.s.armies[h.army];
  if (attacker === undefined) return;

  const defenderHero = townDefender(c, t0);
  if (defenderHero !== null) absorbGarrison(c, t0, defenderHero);
  const t = c.s.towns[townId]!;
  const defenderArmy = defenderHero === null ? t.garrison : defenderHero.army;
  const defenders = c.s.armies[defenderArmy];
  if (defenders === undefined) return;

  const fort = fortLevelOf(c, t);
  const works = townDefence(c, t, fort);
  // Only meaningful when there is masonry to thicken, so it is asked for on
  // the same condition `defenseSiege` is - an open town has no walls to buff.
  const wallBonus = fort > 0
    ? resolveStat(0, defenceModifiers(c, t).filter((m) => m.stat === "wallIntegrity")).value
    : 0;
  // A town with no fort is not a siege at all - The classic fights it on the open
  // field - so it takes the field's scatter rather than `siegeObstacles(0)`,
  // which is the empty list.
  const field = fort > 0 ? { obstacles: siegeObstacles(fort, wallBonus), rng: battleRng(c) } : fieldSetup(c);
  c.s.battle = startBattle({
    attacker: { slots: attacker.slots, hero: heroBattleInput(c, h) },
    defender: {
      slots: defenders.slots,
      hero: defenderHero !== null
        ? withWorks(heroBattleInput(c, defenderHero), works)
        : withWorks({
          id: null, owner: t.owner, attack: 0, defense: 0, spellPower: 0, knowledge: 0,
          mana: 0, spellbook: [], army: defenderArmy,
        }, works),
    },
    terrain: arenaFor(c, t.x, t.y, t.z),
    ground: groundAt(c, t.x, t.y, t.z),
    wetKind: wetKindAt(c, t.x, t.y, t.z, h.boat !== null),
    // The expansion: a Frostmark's walls, gate and towers are each a point tougher. Read
    // as a town modifier so it is a building's to grant, not a faction check.
    obstacles: field.obstacles,
    siege: fort > 0
      ? { active: true, fortLevel: fort, gateOpen: false }
      : null,
    rng: field.rng as never,
    tacticsHexes: tacticsHexesFor(c, h, defenderHero, fort > 0,
      fieldBlindside(c, h, t.x, t.y, t.z)),
  }, c.content);
  for (const e of openBattle(c.s.battle, c.content)) c.events.push(e);
  absorbBattleRng(c);
  // A hero who marches on a town with nothing left to fight with has lost
  // before the first round, and there is no battle command left to fold that
  // outcome back, so it is folded here.
  if (c.s.battle !== null && c.s.battle.outcome.kind !== "ongoing") resolveBattleOutcome(c);
}

// ------------------------------------------------------- Spellcatcher, Scholar

/**
 * Section 5 - the two skills that move a spell from one book into another.
 *
 * Spellcatcher watches: every combat spell the *other* hero casts is a spell this
 * one may copy, at a chance the skill sets and up to a level the skill caps.
 * The roll happens as the cast lands, but the copy goes into `HeroState`, and
 * the battle kernel took its snapshot of the spellbook at `startBattle` - so a
 * spell learned this way is usable from the next battle and not from this one,
 * which is the classic's rule and falls out of the architecture rather than being
 * enforced by it.
 *
 * The roll is on `hero.levelup`, the stream that already carries "a hero
 * learned something". Adding a stream is a save-format change and this is not
 * worth one.
 */
const EAGLE_EYE = asSkillDefId("eagle_eye");
/** Highest spell level Spellcatcher will copy, by skill level. */
export const EAGLE_EYE_LEVEL: readonly number[] = Object.freeze([0, 3, 4, 5]);

function eagleEyeWatch(c: Ctx, events: readonly GameEvent[]): void {
  const b = c.s.battle;
  if (b === null) return;
  for (const e of events) {
    if (e.type !== "BattleSpellCast") continue;
    const watcherId = b.heroes[e.side === 0 ? 1 : 0].id;
    if (watcherId === null) continue;
    const h = c.s.heroes[watcherId];
    if (h === undefined) continue;
    const level = skillLevelOf(c, h, EAGLE_EYE);
    if (level === 0) continue;
    const def = c.content.spells.get(e.spell);
    if (def === undefined || def.level > (EAGLE_EYE_LEVEL[level] ?? 0)) continue;
    if (learnIllegality(c, h, e.spell) !== null) continue;
    const chance = clampInt(statFor(c, h, "eagleEye", 0).value, 0, BP_ONE);
    if (chance <= 0) continue;
    if (!rollChanceBp(c, "hero.levelup", chance)) continue;
    learnSpell(c, watcherId, e.spell);
  }
}

/**
 * Scholar: two of a player's own heroes standing next to each other teach one
 * another everything they may. It fires when a march ends rather than on a
 * command of its own, because in the classic exchange is the meeting.
 */
const SCHOLAR = asSkillDefId("scholar");
/** Highest spell level Scholar will pass across, by skill level. */
export const SCHOLAR_LEVEL: readonly number[] = Object.freeze([0, 2, 3, 4]);

function scholarExchange(c: Ctx, heroId: HeroId): void {
  const h = c.s.heroes[heroId];
  if (h === undefined) return;
  for (const id of entityKeys(c.s.heroes)) {
    const other = c.s.heroes[id]!;
    if (other.id === heroId || other.owner !== h.owner) continue;
    if (other.z !== h.z || Math.abs(other.x - h.x) > 1 || Math.abs(other.y - h.y) > 1) continue;
    teachAcross(c, heroId, other.id);
    teachAcross(c, other.id, heroId);
  }
}

/** Everything `from` may pass to `to`, capped by the teacher's own Scholar. */
function teachAcross(c: Ctx, from: HeroId, to: HeroId): void {
  const teacher = c.s.heroes[from];
  const pupil = c.s.heroes[to];
  if (teacher === undefined || pupil === undefined) return;
  const cap = SCHOLAR_LEVEL[skillLevelOf(c, teacher, SCHOLAR)] ?? 0;
  if (cap === 0) return;
  for (const spell of [...teacher.spellbook].sort()) {
    const def = c.content.spells.get(spell);
    if (def === undefined || def.level > cap) continue;
    const now = c.s.heroes[to];
    if (now === undefined || learnIllegality(c, now, spell) !== null) continue;
    learnSpell(c, to, spell);
  }
}

function battle(c: Ctx, cmd: BattleCommand): Result<true, ExecuteError> {
  const b = c.s.battle;
  if (b === null) return no({ code: "NO_BATTLE_IN_PROGRESS" });

  // Ownership. `GameCommand` carries no player on a BATTLE, so the side that
  // may act is derived from the battle itself: whoever owns the active stack.
  // During tactics both sides place, which is why the check is skipped there.
  if (!b.tacticsPhase) {
    const slot = activeStack(b);
    const activeSide = slot >= 0 ? b.stacks.side[slot]! : -1;
    if ("stack" in cmd) {
      const st = b.stacks;
      if (cmd.stack < 0 || cmd.stack >= st.n) return no({ code: "STACK_DEAD", stack: cmd.stack });
      if (st.side[cmd.stack] !== activeSide) return no({ code: "STACK_NOT_ACTIVE", stack: cmd.stack });
    } else if ("side" in cmd && cmd.side !== activeSide) {
      return no({ code: "NOT_YOUR_TURN" });
    }
  }

  if (cmd.type === "BATTLE_SURRENDER") {
    const cost = surrenderCost(c, cmd.side);
    const owner = b.heroes[cmd.side].owner;
    if (owner !== null) {
      const p = c.s.players[owner];
      if (p === undefined || p.resources.gold < cost) {
        return no({ code: "INSUFFICIENT_RESOURCES", resource: "gold", have: p?.resources.gold ?? 0, need: cost });
      }
    }
  }

  const r = executeBattleCommand(b, cmd, c.content);
  if (!r.ok) return Err(r.error);
  for (const e of r.value.events) c.events.push(e);
  absorbBattleRng(c);
  eagleEyeWatch(c, r.value.events);

  if (c.s.battle !== null && c.s.battle.outcome.kind !== "ongoing") resolveBattleOutcome(c);
  return yes;
}

export function surrenderCost(c: Ctx, side: 0 | 1): number {
  const b = c.s.battle;
  if (b === null) return 0;
  const armyId = b.heroes[side].army;
  const a = armyId === null ? null : c.s.armies[armyId] ?? null;
  const base = armyGoldCost(c.content, a);
  const heroId = b.heroes[side].id;
  const h = heroId === null ? null : c.s.heroes[heroId] ?? null;
  // Diplomacy, plus whatever the hero's kit adds to it: three treasure
  // artifacts each shave a tenth off the asking price and the Parley Cloak
  // takes the whole of it.
  const skillCut = h === null ? 0 : SURRENDER_DIPLOMACY_CUT_BP[skillLevelOf(c, h, CODED_SKILLS.diplomacy)] ?? 0;
  const wornCut = h === null ? 0 : statFor(c, h, "surrenderCut", 0).value;
  const cut = clampInt(skillCut + wornCut, 0, BP_ONE);
  return Math.floor((base * (10000 - cut)) / 10000);
}

/** The object whose guard is fighting, if this battle came from one. */
function battleObject(c: Ctx): MapObjectState | null {
  const b = c.s.battle;
  if (b === null) return null;
  const guard = b.heroes[SIDE_DEFENDER].army;
  if (guard === null) return null;
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id]!;
    if (o.guard === guard) return o;
  }
  return null;
}

function battleTown(c: Ctx): TownState | null {
  const b = c.s.battle;
  if (b === null) return null;
  const army = b.heroes[SIDE_DEFENDER].army;
  if (army === null) return null;
  for (const id of entityKeys(c.s.towns)) {
    const t = c.s.towns[id]!;
    if (t.garrison === army) return t;
    // A town defended by its own hero fields *his* army (`absorbGarrison`), so
    // the town has to be recognised through him as well or the winner would
    // beat the defence and not take the town.
    const hero = t.garrisonHero ?? t.visitingHero;
    if (hero !== null && c.s.heroes[hero]?.army === army) return t;
  }
  return null;
}

/** Survivors, and the aiValue of everything that died, for one side. */
function foldSide(c: Ctx, side: 0 | 1): number {
  const b = c.s.battle!;
  const armyId = b.heroes[side].army;
  if (armyId === null) return 0;
  const before = c.s.armies[armyId];
  if (before === undefined) return 0;

  const survivors = new Map<number, number>();
  for (let i = 0; i < b.stacks.n; i++) {
    if (b.stacks.side[i] !== side) continue;
    const slot = b.stacks.slotInArmy[i]!;
    const alive = isAlive(b.stacks, i) ? b.stacks.count[i]! : 0;
    survivors.set(slot, (survivors.get(slot) ?? 0) + alive);
  }

  let lostValue = 0;
  for (let i = 0; i < before.slots.length; i++) {
    const s = before.slots[i];
    if (s === null || s === undefined) continue;
    const left = survivors.get(i) ?? 0;
    const dead = s.count - left;
    if (dead > 0) {
      const def = c.content.creatures.get(s.def);
      lostValue += (def?.aiValue ?? 0) * dead;
    }
    setSlot(c, armyId, i, left > 0 ? { def: s.def, count: left } : null);
  }
  return lostValue;
}

/**
 * Fold a finished battle back into the adventure state: casualties, experience,
 * artifacts, necromancy, the object or town that was being fought over, and the
 * fate of the losing hero.
 */
export function resolveBattleOutcome(c: Ctx): void {
  const b = c.s.battle;
  if (b === null) return;
  const outcome = b.outcome;
  if (outcome.kind === "ongoing") return;

  const attackerHero = b.heroes[SIDE_ATTACKER].id;
  const defenderHero = b.heroes[SIDE_DEFENDER].id;
  const object = battleObject(c);
  const town = object === null ? battleTown(c) : null;

  const attackerLost = foldSide(c, SIDE_ATTACKER as 0);
  const defenderLost = foldSide(c, SIDE_DEFENDER as 1);

  let winner: 0 | 1 | null = null;
  if (outcome.kind === "victory") winner = outcome.side;
  else if (outcome.kind === "retreat" || outcome.kind === "surrender") {
    winner = outcome.side === SIDE_ATTACKER ? (SIDE_DEFENDER as 1) : (SIDE_ATTACKER as 0);
  }

  if (outcome.kind === "surrender") {
    const loser = outcome.side;
    const owner = b.heroes[loser].owner;
    if (owner !== null) addResource(c, owner, "gold", -surrenderCost(c, loser));
  }

  // Mana spent in the fight is spent in the world. The battle kernel holds no
  // `HeroState`, so the pool it drew down only becomes real when it is read
  // back here; without this a hero could empty his book every fight for free.
  for (const side of [SIDE_ATTACKER, SIDE_DEFENDER] as const) {
    const id = b.heroes[side].id;
    if (id === null || c.s.heroes[id] === undefined) continue;
    const mana = b.heroes[side].mana;
    if (mana >= 0) patchHero(c, id, { mana });
  }

  c.s.battle = null;

  const winnerHero = winner === SIDE_ATTACKER ? attackerHero
    : winner === SIDE_DEFENDER ? defenderHero : null;
  const loserSide: 0 | 1 | null = winner === null ? null
    : winner === SIDE_ATTACKER ? (SIDE_DEFENDER as 1) : (SIDE_ATTACKER as 0);
  const loserHero = loserSide === SIDE_ATTACKER ? attackerHero
    : loserSide === SIDE_DEFENDER ? defenderHero : null;

  if (winnerHero !== null && c.s.heroes[winnerHero] !== undefined) {
    const xp = winner === SIDE_ATTACKER ? defenderLost : attackerLost;
    if (xp > 0) grantExperience(c, winnerHero, xp);
    // The Band of Forgetting's second half: nothing rises after the battle
    // either. `b` is still in scope here, which is the only reason this can be
    // asked - `resolveBattleOutcome` clears the battle further down.
    if (!raisingBarred(b)) {
      raiseUndead(c, winnerHero, winner === SIDE_ATTACKER ? defenderLost : attackerLost);
    }
  }

  // The winner strips the loser, equipped artifacts included (Section 34).
  if (winnerHero !== null && loserHero !== null) {
    const loser = c.s.heroes[loserHero];
    const winnerState = c.s.heroes[winnerHero];
    if (loser !== undefined && winnerState !== undefined) {
      const taken: ArtifactDefId[] = [...loser.backpack];
      for (const k of Object.keys(loser.artifacts).sort()) {
        const a = loser.artifacts[k];
        if (a !== null && a !== undefined) taken.push(a);
      }
      if (taken.length > 0) {
        patchHero(c, winnerHero, { backpack: [...winnerState.backpack, ...taken] });
        for (const a of taken) {
          c.events.push({ type: "ArtifactGained", hero: winnerHero, artifact: a });
        }
      }
    }
  }

  if (loserHero !== null) {
    const preserved = outcome.kind === "retreat" || outcome.kind === "surrender";
    // The two ways out of a lost battle differ in exactly one thing, and it is
    // the army. A hero who runs abandons his troops; a hero who buys his way
    // out keeps them, which is what the gold in the branch above pays for.
    // Both survive and both go back into their own tavern pool.
    if (outcome.kind === "retreat") disbandArmy(c, loserHero);
    retireHero(c, loserHero, preserved, outcome.kind === "surrender");
    // A "defeat this hero" scenario is won by whoever landed the blow. Asked
    // here because only the battle knows the winner: once the hero is gone
    // the day-end check can see that he is gone and not who did it, and it
    // used to fall back to "the last kingdom standing", which is defeat_all.
    if (c.s.victory.condition === `defeat_hero:${loserHero as string}`) {
      const byHero = winnerHero !== null ? c.s.heroes[winnerHero]?.owner ?? null : null;
      const byTown = town !== null && winner === SIDE_DEFENDER ? town.owner : null;
      const w = byHero ?? byTown;
      if (w !== null) declareWinner(c, w);
    }
  }

  if (object !== null && winner === SIDE_ATTACKER && attackerHero !== null) {
    const still = c.s.heroes[attackerHero];
    if (still !== undefined) {
      grantBankReward(c, attackerHero, object);
      c.events.push({ type: "ObjectVisited", hero: attackerHero, object: object.id, def: object.def });
      if (object.kind === "monster") {
        // The classic walks the winner onto the tile the monster was holding. A hero
        // who fought from inside his own town has therefore left it, and the
        // town must stop naming him as its visitor, or recruits keep flowing
        // into an army that is standing in a field.
        if (still.inTown !== null) {
          const t = c.s.towns[still.inTown];
          if (t !== undefined && t.visitingHero === attackerHero) {
            patchTown(c, still.inTown, { visitingHero: null });
          }
        }
        patchHero(c, attackerHero, { x: object.x, y: object.y, z: object.z, inTown: null });
        removeObject(c, object.id);
        revealStep(c, c.s.heroes[attackerHero]!);
      } else {
        // The guard is spent, and the object must remember that: `guardArmyOf`
        // materialises a fresh guard from the def's `count` whenever `guard`
        // is null, which made every cleared bank a mandatory fight and an
        // experience farm for each hero after the first.
        patchObject(c, object.id, { guard: null });
        setPayload(c, object.id, { count: 0 });
        if (object.kind === "garrison") openGarrison(c, object.id, still.owner);
      }
    }
  }

  if (town !== null && winner === SIDE_ATTACKER && attackerHero !== null) {
    const h = c.s.heroes[attackerHero];
    if (h !== undefined) {
      captureTown(c, town.id, h.owner);
      enterTown(c, attackerHero, town.id);
    }
  }

  for (const id of entityKeys(c.s.heroes)) {
    const h = c.s.heroes[id]!;
    const mods = h.modifiers.filter((m) => m.expires?.kind !== "battle" && m.expires?.kind !== "round");
    if (mods.length !== h.modifiers.length) patchHero(c, id as HeroId, { modifiers: mods });
  }
}

/** Everything the hero was leading, gone. Used by retreat, not by surrender. */
function disbandArmy(c: Ctx, heroId: HeroId): void {
  const h = c.s.heroes[heroId];
  if (h === undefined) return;
  const a = c.s.armies[h.army];
  if (a === undefined) return;
  for (let i = 0; i < a.slots.length; i++) setSlot(c, h.army, i, null);
}

/** Gravecalling: a share of the fallen rise for the winner (Section 42). */
function raiseUndead(c: Ctx, heroId: HeroId, enemyValue: number): void {
  const h = c.s.heroes[heroId];
  if (h === undefined || enemyValue <= 0) return;
  const pct = statFor(c, h, "necromancyPct", 0).value;
  if (pct <= 0) return;
  const skeleton = necromancyCreature(c, h);
  if (skeleton === null) return;
  const def = c.content.creatures.get(skeleton)!;
  // Value in, creatures out: the raised stack is worth `pct` percent of what
  // died, which keeps a Bonecaller honest against both peasants and dragons.
  const raised = Math.floor((enemyValue * pct) / (100 * Math.max(1, def.aiValue)));
  if (raised <= 0) return;
  const army = c.s.armies[h.army];
  if (army === undefined || !hasFreeSlotFor(army, skeleton)) return;
  addCreatures(c, h.army, skeleton, raised);
}

export function necromancyCreature(c: Ctx, h: HeroState): CreatureDefId | null {
  // An explicit override wins: whatever the hero's kit says it raises, it
  // raises. Checked before the faction search so the Conch of the Deep works
  // for a hero of any town, which is the whole point of it.
  for (const m of heroModifiers(c, h)) {
    const stat = m.stat as string;
    if (!stat.startsWith("raisesCreature.")) continue;
    const id = stat.slice("raisesCreature.".length) as CreatureDefId;
    if (c.content.creatures.has(id)) return id;
  }
  const faction = c.content.factions.get(h.faction);
  let best: CreatureDefId | null = null;
  let bestTier = 99;
  for (const id of faction?.creatures ?? []) {
    const def = c.content.creatures.get(id);
    if (def === undefined || def.upgraded) continue;
    if (!def.abilities.some((a) => a.kind === "UNDEAD")) continue;
    if (def.tier < bestTier) { bestTier = def.tier; best = id; }
  }
  return best;
}

/**
 * Remove a hero from the map; `preserved` puts them back in the tavern pool,
 * and `keepArmy` leaves his army in state for the re-hire - a surrender buys
 * that, a retreat and a defeat do not.
 */
export function retireHero(c: Ctx, heroId: HeroId, preserved: boolean, keepArmy = false): void {
  const h = c.s.heroes[heroId];
  if (h === undefined) return;
  const owner = c.s.players[h.owner];
  if (owner !== undefined) {
    patchPlayer(c, h.owner, {
      heroes: owner.heroes.filter((x) => x !== heroId),
      tavern: preserved && !owner.tavern.includes(h.def)
        ? [...owner.tavern, h.def].sort()
        : owner.tavern,
    });
  }
  if (h.inTown !== null) {
    const t = c.s.towns[h.inTown];
    if (t !== undefined && t.visitingHero === heroId) {
      patchTown(c, h.inTown, { visitingHero: null });
    }
  }
  // The army goes with him unless it was paid for. A kept army is filed under
  // the hero's def so the Tavern can hand it back with the re-hire; until
  // then `armyCommands` ignores it, because nobody holds it.
  if (keepArmy && owner !== undefined) {
    const fresh = c.s.players[h.owner]!;
    patchPlayer(c, h.owner, { heldArmies: { ...(fresh.heldArmies ?? {}), [h.def as string]: h.army } });
  } else {
    dropArmy(c, h.army);
  }
  dropHero(c, heroId);
  c.events.push({ type: "HeroDefeated", hero: heroId, player: h.owner });
}

export function captureTown(c: Ctx, id: TownId, to: PlayerId): void {
  const t = c.s.towns[id];
  if (t === undefined || t.owner === to) return;
  const from = t.owner;
  if (from !== null) {
    const p = c.s.players[from];
    if (p !== undefined) patchPlayer(c, from, { towns: p.towns.filter((x) => x !== id) });
  }
  const np = c.s.players[to];
  if (np !== undefined) patchPlayer(c, to, { towns: [...np.towns, id].sort() });
  patchTown(c, id, { owner: to, visitingHero: null, garrisonHero: null, autoBuild: false });
  // The hall's garrison army changes hands with the hall. Without this it keeps
  // the loser's owner (or null, for a neutral town), and `armyCommands` - which
  // enumerates only armies whose owner is the acting player - would never offer
  // a TRANSFER/MERGE/SPLIT/DISMISS for it again, while `recruitTargetArmy` goes
  // on feeding recruits into it. That is gold turning into creatures nobody can
  // ever pick up.
  patchArmy(c, t.garrison, { owner: to });
  // One Capitol per player: a conqueror who already has one demotes the one he
  // just took rather than collecting a second four thousand a day.
  const demoted = demoteCapitol(c, id, to);
  if (demoted !== null) c.events.push({ type: "BuildingBuilt", town: id, building: demoted });
  c.events.push({ type: "TownCaptured", town: id, player: to, from });
}

// -------------------------------------------------------------------- town

/**
 * Replace one spell in a guild slot with one of the day's two candidates.
 *
 * The spell that leaves is the FIRST of the slot in sorted order, so the choice
 * of what to discard is not a second decision the player has to make and not a
 * roll: the expansion researches a slot, and a slot here is a sorted list.
 */
function researchSpell(
  c: Ctx, townId: TownId, level: number, pick: number,
): Result<true, ExecuteError> {
  const bad = researchIllegality(c, townId, level, pick);
  if (bad !== null) return no(bad);
  const t = c.s.towns[townId]!;
  const gained = researchCandidates(c, t, level)[pick]!;
  const cost = researchCost(level);
  for (const r of RESOURCES) {
    const v = cost[r] ?? 0;
    if (v > 0) addResource(c, t.owner!, r, -v);
  }
  const guild = t.guildSpells.map((row) => [...row]);
  const slot = guild[level - 1]!;
  const dropped = slot[0]!;
  slot[0] = gained;
  slot.sort();
  patchTown(c, townId, { guildSpells: guild, researchedThisTurn: true });
  c.events.push({ type: "GuildSpellsSet", town: townId, level, spells: [...slot] });
  c.events.push({ type: "SpellResearched", town: townId, level, dropped, gained });
  return yes;
}

function build(c: Ctx, townId: TownId, building: BuildingDefId): Result<true, ExecuteError> {
  const t = c.s.towns[townId];
  if (t === undefined) return no({ code: "NO_SUCH_ENTITY", id: townId as string });
  if (t.owner === null) return no({ code: "NOT_OWNED", id: townId as string });
  const reason = buildIllegality(c, t, building);
  if (reason !== null) return no(reason);
  const def = c.content.buildings.get(building)!;
  for (const r of RESOURCES) {
    const v = def.cost[r] ?? 0;
    if (v > 0) addResource(c, t.owner, r, -v);
  }
  // Section 39: raising the Grail structure spends the relic. A captured town
  // keeps the building; nobody gets the Grail back out of it.
  if (def.slot === GRAIL_SLOT) {
    const bearer = grailBearerIn(c, t);
    if (bearer !== null) spendGrail(c, bearer);
  }
  addBuilding(c, townId, def);
  if (def.guildLevel > 0) fillGuild(c, townId);
  // Whatever was just built, the hero standing in the town gets it now rather
  // than on his next lap through the gate.
  const visiting = c.s.towns[townId]!.visitingHero;
  if (visiting !== null) arriveInTown(c, visiting, townId);
  return yes;
}

function toggleAutoBuild(c: Ctx, townId: TownId): Result<true, ExecuteError> {
  const t = c.s.towns[townId];
  if (t === undefined) return no({ code: "NO_SUCH_ENTITY", id: townId as string });
  if (t.owner === null) return no({ code: "NOT_OWNED", id: townId as string });
  patchTown(c, townId, { autoBuild: !(t.autoBuild ?? false) });
  return yes;
}

function recruit(
  c: Ctx, townId: TownId, creature: CreatureDefId, count: number,
): Result<true, ExecuteError> {
  const t = c.s.towns[townId];
  if (t === undefined) return no({ code: "NO_SUCH_ENTITY", id: townId as string });
  if (t.owner === null) return no({ code: "NOT_OWNED", id: townId as string });
  const def = c.content.creatures.get(creature);
  if (def === undefined) return no({ code: "NO_SUCH_ENTITY", id: creature as string });

  const have = availableOf(c, t, creature);
  if (have < count) return no({ code: "NONE_AVAILABLE", creature, have, need: count });

  const target = recruitTargetArmy(c, t);
  const army = c.s.armies[target];
  if (army === undefined) return no({ code: "NO_SUCH_ENTITY", id: target as string });
  if (!hasFreeSlotFor(army, creature)) return no({ code: "ARMY_FULL" });

  const cost = recruitCost(c.content, creature, count);
  const p = c.s.players[t.owner]!;
  const miss = missingResource(p.resources, cost);
  if (miss !== null) return no({ code: "INSUFFICIENT_RESOURCES", ...miss });

  for (const r of RESOURCES) {
    const v = cost[r] ?? 0;
    if (v > 0) addResource(c, t.owner, r, -v);
  }
  spendAvailable(c, townId, creature, count);
  addCreatures(c, target, creature, count);
  c.events.push({ type: "CreaturesRecruited", town: townId, creature, count });
  return yes;
}


/**
 * Pay to turn a standing stack into its upgraded form, at a town.
 *
 * The counterpart to recruiting the upgraded creature outright: a player who
 * built the upgraded dwelling after already owning the base creature could
 * previously only buy new ones, and the stack they had stayed put for ever.
 *
 * The stack keeps its count and its slot - this is not a purchase, so it spends
 * no weekly growth and needs no free slot. What it costs is the difference
 * between the two creatures' prices, per creature, which is the classic's rule and is
 * computed in `upgrade.ts` so that the Warcraft Keep and the town cannot drift.
 */
function upgradeUnits(
  c: Ctx, townId: TownId, armyId: ArmyId, slotIndex: number,
): Result<true, ExecuteError> {
  const t = c.s.towns[townId];
  if (t === undefined) return no({ code: "NO_SUCH_ENTITY", id: townId as string });
  if (t.owner === null) return no({ code: "NOT_OWNED", id: townId as string });
  // The army must be one this town can reach into: its garrison, or a hero
  // standing in it. Without this a player could upgrade a stack anywhere on the
  // map by naming a town they happen to own.
  if (!upgradableArmies(c, t).includes(armyId)) {
    return no({ code: "NOT_OWNED", id: armyId as string });
  }
  const army = c.s.armies[armyId];
  if (army === undefined) return no({ code: "NO_SUCH_ENTITY", id: armyId as string });

  const stack = army.slots[slotIndex];
  if (stack === null || stack === undefined || stack.count <= 0) {
    return no({ code: "SLOT_EMPTY", slot: slotIndex });
  }

  const offer = townUpgradeOffers(c, t, armyId).find((u) => u.slot === slotIndex);
  if (offer === undefined) {
    // Distinguish "there is no upgrade" from "you have not built it yet": the
    // second is actionable and names the building to raise, which is the whole
    // question a player has when the option is missing.
    const to = upgradeOf(c.content, stack.def);
    if (to === null) return no({ code: "TARGET_INVALID" });
    const dwelling = dwellingBuildingFor(c, t, to.id);
    if (dwelling !== null) return no({ code: "MISSING_PREREQUISITE", building: dwelling });
    return no({ code: "TARGET_INVALID" });
  }

  const p = c.s.players[t.owner]!;
  const miss = missingResource(p.resources, offer.cost);
  if (miss !== null) return no({ code: "INSUFFICIENT_RESOURCES", ...miss });

  for (const r of RESOURCES) {
    const v = offer.cost[r] ?? 0;
    if (v > 0) addResource(c, t.owner, r, -v);
  }
  setSlot(c, armyId, slotIndex, { def: offer.to.id, count: offer.count });
  c.events.push({
    type: "CreaturesUpgraded", town: townId, army: armyId, slot: slotIndex,
    from: offer.from.id, to: offer.to.id, count: offer.count,
  });
  return yes;
}

/** The building in this town's faction that produces `creature`, if any. */
function dwellingBuildingFor(
  c: Ctx, t: TownState, creature: CreatureDefId,
): BuildingDefId | null {
  for (const id of [...c.content.buildings.keys()].sort()) {
    const b = c.content.buildings.get(id);
    if (b === undefined || b.dwellingFor !== creature) continue;
    if (b.faction !== null && b.faction !== t.faction) continue;
    return b.id;
  }
  return null;
}

/** Recruits join the hero standing in the town, else the garrison. */
function recruitTargetArmy(c: Ctx, t: TownState): ArmyId {
  const visiting = t.visitingHero === null ? null : c.s.heroes[t.visitingHero] ?? null;
  if (visiting !== null) return visiting.army;
  const garrisoned = t.garrisonHero === null ? null : c.s.heroes[t.garrisonHero] ?? null;
  if (garrisoned !== null) return garrisoned.army;
  return t.garrison;
}

function hasSlot(c: Ctx, t: TownState, slot: string): boolean {
  for (const id of t.built) {
    const b = c.content.buildings.get(id);
    if (b !== undefined && b.slot === slot) return true;
  }
  return false;
}

function hireHero(c: Ctx, townId: TownId, def: HeroDefId): Result<true, ExecuteError> {
  const t = c.s.towns[townId];
  if (t === undefined) return no({ code: "NO_SUCH_ENTITY", id: townId as string });
  if (t.owner === null) return no({ code: "NOT_OWNED", id: townId as string });
  if (!hasSlot(c, t, "tavern")) return no({ code: "MISSING_PREREQUISITE", building: asBuildingDefId("tavern") });
  if (t.visitingHero !== null) return no({ code: "SLOT_OCCUPIED", slot: 0 });

  const p = c.s.players[t.owner]!;
  const heroDef = asHeroDefId(def as string);
  if (!p.tavern.includes(heroDef)) return no({ code: "NO_SUCH_ENTITY", id: def as string });
  if (p.heroes.length >= MAX_HEROES_PER_PLAYER) {
    return no({ code: "HERO_LIMIT_REACHED", limit: MAX_HEROES_PER_PLAYER });
  }
  if (p.resources.gold < HERO_HIRE_COST) {
    return no({ code: "INSUFFICIENT_RESOURCES", resource: "gold", have: p.resources.gold, need: HERO_HIRE_COST });
  }

  addResource(c, t.owner, "gold", -HERO_HIRE_COST);
  patchPlayer(c, t.owner, { tavern: p.tavern.filter((x) => x !== heroDef) });
  // A Tavern sells a commander and a token escort, never an army: the hero
  // definition's opening army is worth about what the hire costs, and handing
  // it over made 2500 gold the cheapest way in the game to buy troops. See
  // `HeroArmySource` and `rollTavernEscort` in create.ts. The one exception is
  // the army a surrender paid to keep, which comes back with its hero.
  const heldId = p.heldArmies?.[heroDef as string];
  const held = heldId === undefined ? undefined : c.s.armies[heldId];
  const id = held === undefined
    ? createHero(c, t.owner, { def: heroDef, x: t.x, y: t.y, z: t.z }, "escort")
    : createHero(c, t.owner, { def: heroDef, x: t.x, y: t.y, z: t.z, army: held.slots }, "none");
  if (heldId !== undefined) {
    const rest = { ...(c.s.players[t.owner]!.heldArmies ?? {}) };
    delete rest[heroDef as string];
    patchPlayer(c, t.owner, { heldArmies: rest });
    if (held !== undefined) dropArmy(c, heldId);
  }
  patchTown(c, townId, { visitingHero: id });
  patchHero(c, id, { inTown: townId });
  arriveInTown(c, id, townId);
  c.events.push({ type: "HeroHired", hero: id, town: townId, player: t.owner });
  revealStep(c, c.s.heroes[id]!);
  return yes;
}

function heroToTown(c: Ctx, heroId: HeroId, townId: TownId): Result<true, ExecuteError> {
  const h = c.s.heroes[heroId];
  const t = c.s.towns[townId];
  if (h === undefined) return no({ code: "NO_SUCH_ENTITY", id: heroId as string });
  if (t === undefined) return no({ code: "NO_SUCH_ENTITY", id: townId as string });
  if (t.owner !== h.owner) return no({ code: "NOT_OWNED", id: townId as string });
  // A hero aboard a hull lands first; walking into a town from the deck left
  // him inside the walls in sea mode, with no land cell reachable from it.
  if (h.boat !== null) return no({ code: "WRONG_TERRAIN" });
  if (t.visitingHero !== null && t.visitingHero !== heroId) {
    return no({ code: "SLOT_OCCUPIED", slot: 0 });
  }
  if (t.z !== h.z || Math.abs(t.x - h.x) > 1 || Math.abs(t.y - h.y) > 1) {
    return no({ code: "NOT_ADJACENT" });
  }
  patchHero(c, heroId, { x: t.x, y: t.y, z: t.z, inTown: townId });
  patchTown(c, townId, { visitingHero: heroId });
  arriveInTown(c, heroId, townId);
  revealStep(c, c.s.heroes[heroId]!);
  return yes;
}

// -------------------------------------------------------------------- army

function armiesInteractable(c: Ctx, a: ArmyId, b: ArmyId): boolean {
  if (a === b) return true;
  const pos = (id: ArmyId): readonly [number, number, number] | null => {
    for (const hid of entityKeys(c.s.heroes)) {
      const h = c.s.heroes[hid]!;
      if (h.army === id) return [h.x, h.y, h.z];
    }
    for (const tid of entityKeys(c.s.towns)) {
      const t = c.s.towns[tid]!;
      if (t.garrison === id) return [t.x, t.y, t.z];
    }
    for (const oid of entityKeys(c.s.objects)) {
      const o = c.s.objects[oid]!;
      if (o.guard === id) return [o.x, o.y, o.z];
    }
    return null;
  };
  const pa = pos(a), pb = pos(b);
  if (pa === null || pb === null) return false;
  return pa[2] === pb[2] && Math.abs(pa[0] - pb[0]) <= 1 && Math.abs(pa[1] - pb[1]) <= 1;
}

/** True when this army is a hero's and emptying it would strand the hero. */
function isHeroArmy(c: Ctx, id: ArmyId): HeroId | null {
  for (const hid of entityKeys(c.s.heroes)) {
    if (c.s.heroes[hid]!.army === id) return hid as HeroId;
  }
  return null;
}

function ownedArmy(c: Ctx, id: ArmyId): ArmyState | null {
  const a = c.s.armies[id];
  if (a === undefined) return null;
  if (a.owner !== c.s.turn.activePlayer) return null;
  return a;
}

function splitStack(
  c: Ctx, id: ArmyId, from: number, to: number, count: number,
): Result<true, ExecuteError> {
  const a = ownedArmy(c, id);
  if (a === null) return no({ code: "NOT_OWNED", id: id as string });
  const src = a.slots[from];
  if (src === null || src === undefined) return no({ code: "SLOT_EMPTY", slot: from });
  if (from === to) return no({ code: "SLOT_OCCUPIED", slot: to });
  const dst = a.slots[to];
  if (dst !== null && dst !== undefined && dst.def !== src.def) {
    return no({ code: "SLOT_OCCUPIED", slot: to });
  }
  if (count >= src.count) return no({ code: "NONE_AVAILABLE", creature: src.def, have: src.count - 1, need: count });
  setSlot(c, id, from, { def: src.def, count: src.count - count });
  setSlot(c, id, to, { def: src.def, count: (dst?.count ?? 0) + count });
  c.events.push({ type: "StackSplit", army: id, from, to, count });
  return yes;
}

function mergeStack(c: Ctx, id: ArmyId, from: number, to: number): Result<true, ExecuteError> {
  const a = ownedArmy(c, id);
  if (a === null) return no({ code: "NOT_OWNED", id: id as string });
  if (from === to) return no({ code: "SLOT_OCCUPIED", slot: to });
  const src = a.slots[from];
  const dst = a.slots[to];
  if (src === null || src === undefined) return no({ code: "SLOT_EMPTY", slot: from });
  if (dst === null || dst === undefined) return no({ code: "SLOT_EMPTY", slot: to });
  if (src.def !== dst.def) return no({ code: "SLOT_OCCUPIED", slot: to });
  setSlot(c, id, to, { def: dst.def, count: dst.count + src.count });
  setSlot(c, id, from, null);
  c.events.push({ type: "StackMerged", army: id, from, to });
  return yes;
}

function swapStack(c: Ctx, id: ArmyId, x: number, y: number): Result<true, ExecuteError> {
  const a = ownedArmy(c, id);
  if (a === null) return no({ code: "NOT_OWNED", id: id as string });
  if (x === y) return yes;
  const sx = a.slots[x] ?? null;
  const sy = a.slots[y] ?? null;
  if (sx === null && sy === null) return no({ code: "SLOT_EMPTY", slot: x });
  setSlot(c, id, x, sy);
  setSlot(c, id, y, sx);
  return yes;
}

function transferStack(
  c: Ctx, fromArmy: ArmyId, fromSlot: number, toArmy: ArmyId, toSlot: number, count: number,
): Result<true, ExecuteError> {
  const src = ownedArmy(c, fromArmy);
  const dstArmy = c.s.armies[toArmy];
  if (src === null) return no({ code: "NOT_OWNED", id: fromArmy as string });
  if (dstArmy === undefined) return no({ code: "NO_SUCH_ENTITY", id: toArmy as string });
  if (dstArmy.owner !== c.s.turn.activePlayer) return no({ code: "NOT_OWNED", id: toArmy as string });
  if (!armiesInteractable(c, fromArmy, toArmy)) return no({ code: "NOT_ADJACENT" });

  const s = src.slots[fromSlot];
  if (s === null || s === undefined) return no({ code: "SLOT_EMPTY", slot: fromSlot });
  if (count > s.count) {
    return no({ code: "NONE_AVAILABLE", creature: s.def, have: s.count, need: count });
  }
  const d = dstArmy.slots[toSlot] ?? null;
  if (d !== null && d.def !== s.def) return no({ code: "SLOT_OCCUPIED", slot: toSlot });

  const leaving = s.count - count;
  const owner = isHeroArmy(c, fromArmy);
  if (owner !== null && leaving <= 0 && countStacks(src) === 1 && fromArmy !== toArmy) {
    return no({ code: "LAST_STACK" });
  }

  setSlot(c, fromArmy, fromSlot, leaving > 0 ? { def: s.def, count: leaving } : null);
  setSlot(c, toArmy, toSlot, { def: s.def, count: (d?.count ?? 0) + count });
  c.events.push({ type: "StackTransferred", fromArmy, toArmy, creature: s.def, count });
  return yes;
}

function countStacks(a: ArmyState): number {
  let n = 0;
  for (const s of a.slots) if (s !== null && s.count > 0) n++;
  return n;
}

function dismissStack(c: Ctx, id: ArmyId, slot: number): Result<true, ExecuteError> {
  const a = ownedArmy(c, id);
  if (a === null) return no({ code: "NOT_OWNED", id: id as string });
  const s = a.slots[slot];
  if (s === null || s === undefined) return no({ code: "SLOT_EMPTY", slot });
  if (isHeroArmy(c, id) !== null && countStacks(a) === 1) return no({ code: "LAST_STACK" });
  setSlot(c, id, slot, null);
  return yes;
}

// --------------------------------------------------------------- artifacts

function equip(
  c: Ctx, heroId: HeroId, index: number, slot: string,
): Result<true, ExecuteError> {
  const h = c.s.heroes[heroId];
  if (h === undefined) return no({ code: "NO_SUCH_ENTITY", id: heroId as string });
  const art = h.backpack[index];
  if (art === undefined) return no({ code: "SLOT_EMPTY", slot: index });
  const def = c.content.artifacts.get(art);
  if (def === undefined) return no({ code: "NO_SUCH_ENTITY", id: art as string });
  if (!slotAccepts(def, slot)) return no({ code: "TARGET_INVALID" });
  if (!slotIsFree(h, slot)) return no({ code: "SLOT_OCCUPIED", slot: 0 });

  const prevMax = maxMana(c, h);
  patchHero(c, heroId, {
    artifacts: withSlots(h, { [slot]: art }),
    backpack: [...h.backpack.slice(0, index), ...h.backpack.slice(index + 1)],
  });
  retuneMana(c, heroId, prevMax);
  c.events.push({ type: "ArtifactEquipped", hero: heroId, artifact: art, slot });
  return yes;
}

/**
 * Both arms are three lines because the rules are one module. `assemble.ts`
 * owns what a set is, which parts count and which slot the finished artifact
 * lands in; this file owns only the fact that a command reaches them - the same
 * split `castAdventure` is built on.
 */
function assembleCommand(
  c: Ctx, heroId: HeroId, art: ArtifactDefId,
): Result<true, ExecuteError> {
  const h = c.s.heroes[heroId];
  if (h === undefined) return no({ code: "NO_SUCH_ENTITY", id: heroId as string });
  const why = assembleIllegality(c.content, h, art);
  if (why !== null) return no(why);
  const prevMax = maxMana(c, h);
  assemble(c, heroId, art);
  retuneMana(c, heroId, prevMax);
  return yes;
}

function disassembleCommand(
  c: Ctx, heroId: HeroId, slot: string,
): Result<true, ExecuteError> {
  const h = c.s.heroes[heroId];
  if (h === undefined) return no({ code: "NO_SUCH_ENTITY", id: heroId as string });
  const why = disassembleIllegality(c.content, h, slot);
  if (why !== null) return no(why);
  const prevMax = maxMana(c, h);
  disassemble(c, heroId, slot);
  retuneMana(c, heroId, prevMax);
  return yes;
}

/**
 * Two of a player's own heroes standing next to each other, and one of them
 * hands over a bagged artifact.
 *
 * The adjacency is `scholarExchange`'s and `armiesInteractable`'s - same level,
 * Chebyshev distance at most one, distance zero included - so a meeting is a
 * meeting whether the heroes are side by side or (a state the kernel used to
 * allow and no longer does) sharing a cell. Both heroes must belong to the
 * player whose turn it is: there is no alliance in this kernel, so "allied
 * hero" can only mean a second hero of your own.
 *
 * The artifact lands in the receiver's BAG, never in a free slot of his. Wearing
 * it is a separate decision with its own legality - a Spellweaver's Hat over a
 * worn helm is a refusal, not a silent swap - and `EQUIP_ARTIFACT` already owns
 * that decision. The bag has no cap anywhere in this kernel, so nothing here
 * can bounce for want of room.
 */
export function heroesMeeting(a: HeroState, b: HeroState): boolean {
  return a.owner === b.owner
    && a.z === b.z
    && Math.abs(a.x - b.x) <= 1
    && Math.abs(a.y - b.y) <= 1;
}

function passArtifact(
  c: Ctx, fromHero: HeroId, toHero: HeroId, index: number,
): Result<true, ExecuteError> {
  const from = c.s.heroes[fromHero];
  const to = c.s.heroes[toHero];
  if (from === undefined) return no({ code: "NO_SUCH_ENTITY", id: fromHero as string });
  if (to === undefined) return no({ code: "NO_SUCH_ENTITY", id: toHero as string });
  if (fromHero === toHero) return no({ code: "TARGET_INVALID" });
  if (from.owner !== c.s.turn.activePlayer) return no({ code: "NOT_OWNED", id: fromHero as string });
  if (to.owner !== c.s.turn.activePlayer) return no({ code: "NOT_OWNED", id: toHero as string });
  if (!heroesMeeting(from, to)) return no({ code: "NOT_ADJACENT" });

  const art = from.backpack[index];
  if (art === undefined) return no({ code: "SLOT_EMPTY", slot: index });

  patchHero(c, fromHero, {
    backpack: [...from.backpack.slice(0, index), ...from.backpack.slice(index + 1)],
  });
  patchHero(c, toHero, { backpack: [...to.backpack, art] });
  c.events.push({ type: "ArtifactGiven", hero: fromHero, to: toHero, artifact: art });
  return yes;
}

function unequip(c: Ctx, heroId: HeroId, slot: string): Result<true, ExecuteError> {
  const h = c.s.heroes[heroId];
  if (h === undefined) return no({ code: "NO_SUCH_ENTITY", id: heroId as string });
  const art = h.artifacts[slot];
  if (art === null || art === undefined) return no({ code: "SLOT_EMPTY", slot: 0 });
  const prevMax = maxMana(c, h);
  patchHero(c, heroId, { artifacts: withSlots(h, { [slot]: null }), backpack: [...h.backpack, art] });
  retuneMana(c, heroId, prevMax);
  return yes;
}

// ------------------------------------------------------------ adventure spells

/**
 * The adventure-spell arm is one line because the rules are one module.
 * `spells.ts` owns the gate, the mastery tables and the ten rules; this file
 * owns only the fact that `CAST_ADVENTURE_SPELL` reaches them.
 */
function castAdventure(
  c: Ctx, heroId: HeroId, spell: SpellDefId, x: number, y: number, z: number,
): Result<true, ExecuteError> {
  const out = castAdventureSpell(c, heroId, spell, x, y, z);
  if (!out.ok) return out;
  // Homeward Gate can land a hero in a town, and arriving in a town is what
  // teaches its guild - the same path a hero who walked in takes.
  const h = c.s.heroes[heroId];
  if (h !== undefined && h.inTown !== null) arriveInTown(c, heroId, h.inTown);
  return yes;
}

// ------------------------------------------------------------------ objects

function visitCommand(c: Ctx, heroId: HeroId, objectId: ObjectId): Result<true, ExecuteError> {
  const h = c.s.heroes[heroId];
  const o = c.s.objects[objectId];
  if (h === undefined) return no({ code: "NO_SUCH_ENTITY", id: heroId as string });
  if (o === undefined || o.removed) return no({ code: "NO_SUCH_ENTITY", id: objectId as string });
  if (o.z !== h.z || Math.abs(o.x - h.x) > 1 || Math.abs(o.y - h.y) > 1) {
    return no({ code: "NOT_ADJACENT" });
  }
  const def = c.content.mapObjects.get(o.def);
  if (def?.oneVisitPerHero === true && h.visited.includes(objectId)) {
    return no({ code: "ALREADY_VISITED", object: objectId });
  }
  const out = visitObject(c, heroId, objectId, {});
  if (out.kind === "blocked") return no(out.reason);
  if (out.kind === "battle") startObjectBattle(c, heroId, out.object);
  return yes;
}

function buyArtifact(
  c: Ctx, heroId: HeroId, objectId: ObjectId, artifact: ArtifactDefId,
): Result<true, ExecuteError> {
  const h = c.s.heroes[heroId];
  const o = c.s.objects[objectId];
  if (h === undefined) return no({ code: "NO_SUCH_ENTITY", id: heroId as string });
  if (o === undefined || o.removed) return no({ code: "NO_SUCH_ENTITY", id: objectId as string });
  if (o.z !== h.z || Math.abs(o.x - h.x) > 1 || Math.abs(o.y - h.y) > 1) {
    return no({ code: "NOT_ADJACENT" });
  }
  const pay = payloadOf(c, o);
  if (payStr(pay, "artifact") !== (artifact as string)) return no({ code: "TARGET_INVALID" });
  const def = c.content.artifacts.get(artifact);
  if (def === undefined) return no({ code: "NO_SUCH_ENTITY", id: artifact as string });
  const price = payNum(pay, "price", def.cost);
  const p = c.s.players[h.owner]!;
  if (p.resources.gold < price) {
    return no({ code: "INSUFFICIENT_RESOURCES", resource: "gold", have: p.resources.gold, need: price });
  }
  addResource(c, h.owner, "gold", -price);
  giveArtifact(c, heroId, artifact);
  return yes;
}

/**
 * Section 27 - the shop counter in a town.
 *
 * The Blacksmith is where a hero buys the war machine he takes to the field,
 * and a Mage Guild is where he replaces a spellbook that was taken off him.
 * Both are the same transaction over the same list: a built building's `sells`,
 * priced at the artifact's own cost. No rule here names a building or an
 * artifact, so a faction that sells something else is a data edit.
 *
 * A bought artifact goes straight into its slot when the slot is free, because
 * a war machine in the backpack is a war machine that stays at home and the
 * one-click purchase is the whole point of the counter.
 */
export function townShopStock(c: Ctx, t: TownState): readonly ArtifactDefId[] {
  const out: ArtifactDefId[] = [];
  for (const b of builtDefs(c, t)) {
    for (const a of b.sells) if (!out.includes(a)) out.push(a);
  }
  out.sort();
  return out;
}

/** Why this hero may not buy this artifact from this town, or null. */
export function townBuyIllegality(
  c: Ctx, h: HeroState, t: TownState, artifact: ArtifactDefId,
): Reason | null {
  if (t.owner !== h.owner) return { code: "NOT_OWNED", id: t.id as string };
  if (h.inTown !== t.id) return { code: "NOT_ADJACENT" };
  if (!townShopStock(c, t).includes(artifact)) return { code: "TARGET_INVALID" };
  const def = c.content.artifacts.get(artifact);
  if (def === undefined) return { code: "NO_SUCH_ENTITY", id: artifact as string };
  // Nobody needs two of the same machine, and the slot is what says so.
  if (freeSlotFor(h, def) === null) return { code: "SLOT_OCCUPIED", slot: 0 };
  if (h.backpack.includes(artifact)) return { code: "TARGET_INVALID" };
  const p = c.s.players[h.owner];
  if (p === undefined) return { code: "NO_SUCH_ENTITY", id: h.owner as string };
  if (p.resources.gold < def.cost) {
    return { code: "INSUFFICIENT_RESOURCES", resource: "gold", have: p.resources.gold, need: def.cost };
  }
  return null;
}

function buyTownArtifact(
  c: Ctx, heroId: HeroId, townId: TownId, artifact: ArtifactDefId,
): Result<true, ExecuteError> {
  const h = c.s.heroes[heroId];
  const t = c.s.towns[townId];
  if (h === undefined) return no({ code: "NO_SUCH_ENTITY", id: heroId as string });
  if (t === undefined) return no({ code: "NO_SUCH_ENTITY", id: townId as string });
  const why = townBuyIllegality(c, h, t, artifact);
  if (why !== null) return no(why);
  const def = c.content.artifacts.get(artifact)!;
  addResource(c, h.owner, "gold", -def.cost);
  // `townBuyIllegality` said a socket is free, so this wears it (a bought
  // machine parked in the bag looks like a purchase that failed).
  giveArtifact(c, heroId, artifact);
  return yes;
}

// -------------------------------------------------------------------- dig

/**
 * Section 39 - digging for the Grail.
 *
 * A dig is a whole day: the hero must still have his full movement and the dig
 * spends all of it. That is the only thing that makes the search a decision -
 * without it a hero would sweep a dozen cells an afternoon and the puzzle map
 * would be decoration. Everything else about where the Grail is lives in
 * `grail.ts`.
 */
export function digIllegality(c: Ctx, h: HeroState): Reason | null {
  if (h.boat !== null || h.inTown !== null) return { code: "WRONG_TERRAIN" };
  const cell = indexOf(c.s.map, h.x, h.y, h.z);
  if (!diggableCell(c.s, cell)) return { code: "WRONG_TERRAIN" };
  if (carriesGrail(h)) return { code: "TARGET_INVALID" };
  const full = dailyMovement(c, h);
  if (h.movementLeft < full) {
    return { code: "INSUFFICIENT_MOVEMENT", have: h.movementLeft, need: full };
  }
  return null;
}

function dig(c: Ctx, heroId: HeroId): Result<true, ExecuteError> {
  const h = c.s.heroes[heroId];
  if (h === undefined) return no({ code: "NO_SUCH_ENTITY", id: heroId as string });
  const why = digIllegality(c, h);
  if (why !== null) return no(why);

  patchHero(c, heroId, { movementLeft: 0 });
  c.events.push({ type: "HeroMovementSpent", hero: heroId, left: 0 });
  markDigSite(c, h.x, h.y, h.z);
  const here = indexOf(c.s.map, h.x, h.y, h.z);
  if (here !== grailCell(c.s)) {
    c.events.push({ type: "Message", player: h.owner, text: "dig:nothing" });
    return yes;
  }
  // The Grail is carried, never worn: it is a thing to bring to a town, and
  // the town is what spends it. Into the bag, whatever sockets are free.
  giveArtifact(c, heroId, GRAIL_ARTIFACT, false);
  c.events.push({ type: "Message", player: h.owner, text: "dig:grail" });
  return yes;
}

/**
 * Leave the hole. It stays for the rest of the game.
 *
 * Scanned for rather than looked up, because a dig site is deliberately not in
 * the map's occupant array (see `DIG_SITE_OBJECT`) and there is therefore no
 * index to ask. A dig happens a handful of times in a whole game and the scan
 * is over the object map once, which is a price worth paying to keep the Grail
 * where the seed put it.
 *
 * Silently does nothing when the content has no such def, so a content tree
 * that has not been rebuilt yet loses the mark rather than the dig.
 */
function markDigSite(c: Ctx, x: number, y: number, z: number): void {
  if (c.content.mapObjects.get(DIG_SITE_OBJECT) === undefined) return;
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id]!;
    if (!o.removed && o.def === DIG_SITE_OBJECT && o.x === x && o.y === y && o.z === z) return;
  }
  putObject(c, {
    id: mintObjectId(c), kind: "obstacle", def: DIG_SITE_OBJECT,
    x, y, z, owner: null, payload: {}, guard: null, visitedBy: [], removed: false,
  });
}

function dismissHero(c: Ctx, heroId: HeroId): Result<true, ExecuteError> {
  const h = c.s.heroes[heroId];
  if (h === undefined) return no({ code: "NO_SUCH_ENTITY", id: heroId as string });
  retireHero(c, heroId, false);
  c.events.push({ type: "HeroDismissed", hero: heroId });
  return yes;
}

// ---------------------------------------------------------------- market

function marketplaceCount(c: Ctx, p: PlayerId): number {
  let n = 0;
  for (const id of entityKeys(c.s.towns)) {
    const t = c.s.towns[id]!;
    if (t.owner !== p) continue;
    if (hasSlot(c, t, "marketplace")) n++;
  }
  // A Trading Post is a Marketplace the player found rather than built, and the
  // rate reads the count and nothing else.
  return n + tradingPosts(c, p);
}

/** Value-for-value with a spread that narrows as the player builds Marketplaces. */
export function tradeYield(
  c: Ctx, p: PlayerId, give: ResourceKind, take: ResourceKind, amount: number,
): number {
  const markets = marketplaceCount(c, p);
  const eff = MARKET_EFFICIENCY_BP[markets < MARKET_EFFICIENCY_BP.length ? markets : MARKET_EFFICIENCY_BP.length - 1]!;
  const vg = c.content.resourceValue[give] ?? 1;
  const vt = c.content.resourceValue[take] ?? 1;
  if (vt <= 0) return 0;
  return Math.floor((amount * vg * eff) / (vt * 10000));
}

function trade(
  c: Ctx, p: PlayerId, give: ResourceKind, take: ResourceKind, amount: number,
): Result<true, ExecuteError> {
  if (give === take) return no({ code: "TARGET_INVALID" });
  const pl = c.s.players[p];
  if (pl === undefined) return no({ code: "NO_SUCH_ENTITY", id: p as string });
  if (marketplaceCount(c, p) === 0) {
    return no({ code: "MISSING_PREREQUISITE", building: asBuildingDefId("marketplace") });
  }
  if (pl.resources[give] < amount) {
    return no({ code: "INSUFFICIENT_RESOURCES", resource: give, have: pl.resources[give], need: amount });
  }
  const got = tradeYield(c, p, give, take, amount);
  if (got <= 0) return no({ code: "TARGET_INVALID" });
  addResource(c, p, give, -amount);
  addResource(c, p, take, got);
  return yes;
}

// ------------------------------------------------------------- invariants

/**
 * The DEV sweep. Everything here is a rule the kernel guarantees, so a hit is a
 * bug in this file and never a bad command - which is why it throws.
 */
export function checkInvariants(s: GameState): string | null {
  for (const id of entityKeys(s.heroes)) {
    const h = s.heroes[id]!;
    if (h.movementLeft < 0) return `hero ${id} has negative movement`;
    if (h.mana < 0) return `hero ${id} has negative mana`;
    if (s.armies[h.army] === undefined) return `hero ${id} has no army`;
    if (s.players[h.owner] === undefined) return `hero ${id} has no owner`;
    if (!Number.isInteger(h.x) || !Number.isInteger(h.y) || !Number.isInteger(h.z)) {
      return `hero ${id} has a non-integer position`;
    }
  }
  for (const id of entityKeys(s.armies)) {
    const a = s.armies[id]!;
    if (a.slots.length !== ARMY_SLOTS) return `army ${id} has ${a.slots.length} slots`;
    for (const sl of a.slots) if (sl !== null && sl.count < 0) return `army ${id} has a negative stack`;
  }
  for (const id of entityKeys(s.players)) {
    const p = s.players[id]!;
    for (const r of RESOURCES) if (p.resources[r] < 0) return `player ${id} has negative ${r}`;
    for (const h of p.heroes) if (s.heroes[h] === undefined) return `player ${id} lists a dead hero`;
    for (const t of p.towns) if (s.towns[t] === undefined) return `player ${id} lists a missing town`;
  }
  for (const id of entityKeys(s.towns)) {
    const t = s.towns[id]!;
    const g = s.armies[t.garrison];
    if (g === undefined) return `town ${id} has no garrison army`;
    // A hall and its garrison must be owned by the same player (or both
    // unowned). `armyCommands` enumerates by army owner, so a garrison whose
    // owner drifted from its town's is an army nobody can ever command again.
    if (g.owner !== t.owner) return `town ${id} garrison is owned by ${String(g.owner)}`;
  }
  if (s.pending !== null && s.battle !== null) return "a prompt and a battle are both open";
  return null;
}

